import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams, Navigate, Link } from 'react-router-dom';
import { fetchCategories, createCategory, updateCategory, deleteCategory, moveCategory } from '../services/categoriesService';
import {
  fetchProducts,
  fetchProduct,
  fetchAllContentsByPaginating,
  createProduct,
  updateProduct,
  deactivateProduct,
  activateProduct,
  searchMainProducts,
  searchContentItems,
  getParcaFotoUrl,
  deleteParcaFoto,
} from '../services/productsService';
import { fetchSetting } from '../services/settingsService';
import { fetchBirimler } from '../services/birimService';
import { fetchChecklists } from '../services/checklistsService';
import UrunCokluSecimModal from '../components/UrunCokluSecimModal';
import IcerikExcelImportModal from '../components/IcerikExcelImportModal';
import IcerikExcelEditModal from '../components/IcerikExcelEditModal';
import { downloadIcerikEditExcel } from '../utils/icerikExcelExport';
import CategoryHedefSecModal from '../components/CategoryHedefSecModal';
import ContentFotoUploadZone from '../components/ContentFotoUploadZone';
import ImageEditorModal from '../components/ImageEditorModal';
import ParcaBulkFotoModal from '../components/ParcaBulkFotoModal';
import ParcaBulkFiyatModal from '../components/ParcaBulkFiyatModal';
import { sanitizeSearchInput } from '../utils/security';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import { buildCategoryTree, isCategoryAncestorOf } from '../utils/categoryTreeUtils';
import {
  LIST_SORT_DEFAULT,
  LIST_SORT_OPTIONS,
  parseListSortParam,
  sortContentsRows,
} from '../utils/contentListSort';
// Sanitization backend'de yapılacak, frontend'de encode/decode yapılmıyor

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  if (error?.status === 404) return 'API endpoint bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';

  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

// İçerik tipine göre label adlarını döndür (field adları artık hepsi aynı: icerikAdi, icerikKodu, aciklama)
const getFieldNames = (icerikTipi) => {
  const labels = {
    urun: {
      labelKod: 'Ürün Kodu',
      labelAdi: 'Ürün Adı',
      labelAciklamasi: 'Ürün Açıklaması',
    },
    parca: {
      labelKod: 'Parça Kodu',
      labelAdi: 'Parça Adı',
      labelAciklamasi: 'Parça Açıklaması',
    },
    islem: {
      labelKod: 'İşlem Kodu',
      labelAdi: 'İşlem Adı',
      labelAciklamasi: 'İşlem Açıklaması',
    },
    ariza: {
      labelKod: 'Arıza Kodu',
      labelAdi: 'Arıza Adı',
      labelAciklamasi: 'Arıza Açıklaması',
    },
  };
  
  return labels[icerikTipi] || labels.urun;
};

// Response'tan içerik adını al (backward compatibility için hem yeni hem eski field adlarını kontrol eder)
const getContentName = (item) => {
  return item.icerikAdi || item.urunAdi || item.parcaAdi || item.islemAdi || item.arizaAdi || item.ad || '—';
};

// Response'tan içerik kodunu al (backward compatibility için hem yeni hem eski field adlarını kontrol eder)
const getContentCode = (item) => {
  return item.icerikKodu || item.urunKodu || item.parcaKodu || item.islemKodu || item.arizaKodu || item.kod || '—';
};

const getParcaBirimLabel = (item) => {
  if (!item) return '—';
  const b = item.birim;
  if (b && typeof b === 'object') return b.ad || b.kod || '—';
  return '—';
};

// Response'tan içerik açıklamasını al (backward compatibility için hem yeni hem eski field adlarını kontrol eder)
const getContentMalKodu = (item) => {
  const v = item?.malKodu;
  return v != null && String(v).trim() !== '' ? String(v) : '—';
};

const formatContentPrice = (val) => {
  if (val === undefined || val === null || val === '') return '—';
  return `${Number(val).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
};

const emptyItemFormFields = () => ({
  adi: '',
  kodu: '',
  aciklamasi: '',
  fiyat: '',
  fiyatgaranti: '',
  fiyatGarantiDahili: '',
  fiyatGarantiHarici: '',
  birimId: '',
  foto: null,
  fotoSil: false,
  degisimParcaGarantisi: false,
  bagliUrunOzet: [],
});

const getContentModelKodu = (item) => {
  const v = item?.modelKodu;
  return v != null && String(v).trim() !== '' ? String(v) : '—';
};

const getContentDescription = (item) => {
  const raw =
    item.aciklama ??
    item.urunAciklamasi ??
    item.parcaAciklamasi ??
    item.islemAciklamasi ??
    item.arizaAciklamasi;
  if (raw == null) return '';
  const s = String(raw).trim();
  if (s === '' || s.toLowerCase() === 'null') return '';
  return s;
};

const HighlightMatch = ({ text, query }) => {
  if (!query || query.length < 2 || !text) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${escaped})`, 'gi'));
  if (parts.length <= 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="search-highlight">{part}</mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
};

/**
 * Arama modunda ağaç: yalnızca doğrudan veya alt ağaçta listelenen içeriği olan kategoriler kalır.
 */
const filterCategoryTreeWithVisibleContent = (categoryTree, itemsByCategoryId) => {
  if (!Array.isArray(categoryTree) || categoryTree.length === 0) return [];

  const filterNode = (node) => {
    const idStr = String(node.id || node._id);
    const directCount = (itemsByCategoryId.get(idStr) || []).length;
    const filteredChildren = (node.children || [])
      .map((ch) => filterNode(ch))
      .filter(Boolean);
    if (filteredChildren.length === 0 && directCount === 0) {
      return null;
    }
    return {
      ...node,
      children: filteredChildren,
    };
  };

  return categoryTree.map((n) => filterNode(n)).filter(Boolean);
};

const mergeCategoriesDedup = (prev, incoming) => {
  const byId = new Map();
  (prev || []).forEach((c) => {
    const id = c.id || c._id;
    if (id != null && id !== '') byId.set(String(id), c);
  });
  (incoming || []).forEach((c) => {
    const id = c.id || c._id;
    if (id != null && id !== '') byId.set(String(id), c);
  });
  return Array.from(byId.values());
};

const ACCORDION_PAGE_LIMIT = 100;
const fetchAllIcerikForAccordion = async (fetchPage) => {
  let page = 1;
  const aggregated = [];
  let reportedTotal = null;
  while (true) {
    const response = await fetchPage(page, ACCORDION_PAGE_LIMIT);
    if (response && response.basarili === false) {
      throw new Error(response.mesaj || response.hata || 'Liste alınamadı');
    }
    const chunk =
      response?.veri?.icerikler ||
      response?.veri?.urunler ||
      response?.veri?.parcalar ||
      response?.veri?.islemler ||
      response?.veri?.arizalar ||
      response?.veri ||
      [];
    const list = Array.isArray(chunk) ? chunk : [];
    aggregated.push(...list);
    const pagination = response?.veri?.sayfalama || {};
    reportedTotal = pagination.toplam ?? response?.veri?.toplam ?? reportedTotal;
    if (list.length < ACCORDION_PAGE_LIMIT) break;
    if (reportedTotal != null && aggregated.length >= reportedTotal) break;
    page += 1;
    if (page > 250) break;
  }
  return aggregated;
};

const VALID_CONTENT_TYPES = ['urun', 'parca', 'islem', 'ariza'];

const PAGE_TITLES = {
  urun: 'Ürün Yönetimi',
  parca: 'Parça Yönetimi',
  islem: 'İşlem Yönetimi',
  ariza: 'Arıza Yönetimi',
};

const ContentTypePage = () => {
  const { confirm } = useConfirmDialog();
  const { contentType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = contentType;
  
  const urlAktif = searchParams.get('aktif');
  const urlGorunum = searchParams.get('gorunum');
  
  const isAktifFromUrl = urlAktif !== 'false';
  const gorunumFromUrl = urlGorunum === 'urun' ? 'urun' : 'kategori';
  const listSort = parseListSortParam(searchParams.get('sira'));

  const activeSubTab = isAktifFromUrl ? 'ana-urunler' : 'pasif-urunler';
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Arama kutusu (anında)
  const [debouncedListSearch, setDebouncedListSearch] = useState(''); // Liste API; yarış / 1–2 harf tam-liste isteklerini önler
  const [searchLoading, setSearchLoading] = useState(false);
  /** Gezinmede yaprak kategori içerik listesi yüklenirken (tablo yerine placeholder) */
  const [leafContentsLoadingIds, setLeafContentsLoadingIds] = useState(() => new Set());
  const accordionListFetchGenRef = useRef(0);
  
  // Ürün listesi (API'den gelecek) - sadece ürün sekmesi aktifken dolu
  const [products, setProducts] = useState([]);
  const [contentsByCategoryId, setContentsByCategoryId] = useState({});
  const [confirmedLeafCategoryIds, setConfirmedLeafCategoryIds] = useState(() => new Set());
  const [pickerCategories, setPickerCategories] = useState(null);
  const fetchedCategoryChildrenRef = useRef(new Set());
  const contentsLoadedForCategoryRef = useRef(new Set());
  const confirmedLeafRef = useRef(new Set());
  const prevSearchActiveRef = useRef(false);
  const [categories, setCategories] = useState([]); // Sadece aktif sekme için kategoriler
  
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
  });
  
  // Parça, İşlem, Arıza için liste state - sadece ilgili sekme aktifken dolu
  const [items, setItems] = useState([]); // parca, islem, ariza listesi
  const activeItemSubTab = isAktifFromUrl ? 'aktif' : 'pasif';
  const itemListeModu = gorunumFromUrl;
  
  /** Hedef kategori taşıma modalı: { kind: 'kategori'|'icerik'|'icerik-bulk', category?, item?, ids? } */
  const [moveTargetModal, setMoveTargetModal] = useState(null);
  /** Kategorisiz / toplu taşıma veya toplu aktif-pasif — API sırası */
  const [bulkMoveInProgress, setBulkMoveInProgress] = useState(false);
  /** Seçili parçalara toplu foto — { ids: string[] } */
  const [bulkFotoModal, setBulkFotoModal] = useState(null);
  const [bulkFotoFile, setBulkFotoFile] = useState(null);
  const [bulkFotoInProgress, setBulkFotoInProgress] = useState(false);
  /** Seçili parçalara toplu brüt fiyat — { ids: string[] } */
  const [bulkFiyatModal, setBulkFiyatModal] = useState(null);
  const [bulkFiyatInProgress, setBulkFiyatInProgress] = useState(false);
  /** Ürün / parça: kategori bazlı toplu seçim — yalnızca ilgili kategorinin doğrudan içerikleri */
  const [contentBulkSelectCategoryId, setContentBulkSelectCategoryId] = useState(null);
  const [contentBulkSelectedIds, setContentBulkSelectedIds] = useState(() => new Set());
  const supportsContentBulkSelect = activeTab === 'urun' || activeTab === 'parca';
  
  // Kategori CRUD state
  const [isCategoryFormModalOpen, setIsCategoryFormModalOpen] = useState(false);
  const [categoryFormMode, setCategoryFormMode] = useState('create');
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState(null);
  const [selectedParentCategoryForCreate, setSelectedParentCategoryForCreate] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({ kategoriAdi: '', malKodu: '', modelKodu: '' });
  const [categoryFormError, setCategoryFormError] = useState('');
  const [categoryFormSubmitting, setCategoryFormSubmitting] = useState(false);
  const [linkedPartsByProductId, setLinkedPartsByProductId] = useState({});
  const [linkedPartsLoadingIds, setLinkedPartsLoadingIds] = useState(() => new Set());
  const [isUrunPickerModalOpen, setIsUrunPickerModalOpen] = useState(false);
  
  // Modal states
  const [isProductFormModalOpen, setIsProductFormModalOpen] = useState(false); // Ürün form modalı
  const [isItemFormModalOpen, setIsItemFormModalOpen] = useState(false); // Parça/İşlem/Arıza form modalı
  const [isCategorySelectModalOpen, setIsCategorySelectModalOpen] = useState(false); // Kategori seçim modalı
  const [isMainProductSearchModalOpen, setIsMainProductSearchModalOpen] = useState(false); // Ana ürün arama modalı
  /** Kategori bazlı görünümde yaprak satırdan Excel içe aktarma ({ id, name }) */
  const [excelImportCategory, setExcelImportCategory] = useState(null);
  /** Ürün bazlı parça Excel: { id, kod, ad } */
  const [excelImportUrunContext, setExcelImportUrunContext] = useState(null);
  /** Excel ile düzenle modalı: { contentType, scope } */
  const [excelEditContext, setExcelEditContext] = useState(null);
  
  const [productFormMode, setProductFormMode] = useState('create'); // create, edit
  const [itemFormMode, setItemFormMode] = useState('create'); // create, edit (parca/islem/ariza için)
  const [selectedProduct, setSelectedProduct] = useState(null); // Düzenleme için seçili ürün
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState(null); // Parça/ürün tablosunda thumb tıklanınca modal için
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [expandedCategoryIds, setExpandedCategoryIds] = useState(new Set());
  const [uncategorizedExpanded, setUncategorizedExpanded] = useState(false);
  const [selectedProductForParts, setSelectedProductForParts] = useState(null);
  const [urunTreeSearchInput, setUrunTreeSearchInput] = useState('');
  const [debouncedUrunTreeSearch, setDebouncedUrunTreeSearch] = useState('');
  const [urunTreeSearchResults, setUrunTreeSearchResults] = useState(null); // null | Array
  const [urunTreeCollapsed, setUrunTreeCollapsed] = useState(() => {
    try {
      return localStorage.getItem('hlm-ctp-urun-tree-collapsed') === '1';
    } catch {
      return false;
    }
  });
  const [urunLinkedCollapsed, setUrunLinkedCollapsed] = useState(() => {
    try {
      return localStorage.getItem('hlm-ctp-urun-linked-collapsed') === '1';
    } catch {
      return false;
    }
  });
  const [categoryLockedInModal, setCategoryLockedInModal] = useState(false);
  const [formData, setFormData] = useState({
    icerikAdi: '',
    urunAdi2: '',
    icerikKodu: '',
    aciklama: '',
    kurulumGarantiSuresiAy: '24',
    malKodu: '',
    modelKodu: '',
    checklistId: '',
    foto: null,
    fotoSil: false,
  });
  const [itemFormData, setItemFormData] = useState(emptyItemFormFields());
  const [birimler, setBirimler] = useState([]);
  /** Ürün formu: checklist şablonları (Yönetim → Checklistler ile aynı API) */
  const [urunChecklistSecenekleri, setUrunChecklistSecenekleri] = useState([]);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bagliUrunSaving, setBagliUrunSaving] = useState(false);
  const bagliUrunPersistSeqRef = useRef(0);
  const [urunPickerSyncKey, setUrunPickerSyncKey] = useState(0);
  /** Brüt fiyat önizlemesi için (İskonto Ayarı sayfasındaki ISKONTO_ORANI) */
  const [iskontoOrani, setIskontoOrani] = useState(0);
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [imageEditorSourceFile, setImageEditorSourceFile] = useState(null);
  const [imageEditorTarget, setImageEditorTarget] = useState(null); // 'urun' | 'parca' | 'bulk-parca'

  const openImageEditor = useCallback((file, target) => {
    if (!file || !String(file.type || '').startsWith('image/')) return;
    setImageEditorSourceFile(file);
    setImageEditorTarget(target);
    setImageEditorOpen(true);
  }, []);

  const closeImageEditor = useCallback(() => {
    setImageEditorOpen(false);
    setImageEditorSourceFile(null);
    setImageEditorTarget(null);
  }, []);

  const handleImageEditorConfirm = useCallback(
    (editedFile) => {
      if (imageEditorTarget === 'urun') {
        setFormData((prev) => ({ ...prev, foto: editedFile }));
      } else if (imageEditorTarget === 'parca') {
        setItemFormData((prev) => ({ ...prev, foto: editedFile }));
      } else if (imageEditorTarget === 'bulk-parca') {
        setBulkFotoFile(editedFile);
      }
      closeImageEditor();
    },
    [imageEditorTarget, closeImageEditor]
  );

  const urunFotoPreviewUrl = useMemo(() => {
    if (formData.foto instanceof File) {
      return URL.createObjectURL(formData.foto);
    }
    if (productFormMode === 'edit' && selectedProduct?.fotoUrl && !formData.fotoSil) {
      return getParcaFotoUrl(selectedProduct.fotoUrl);
    }
    return null;
  }, [formData.foto, formData.fotoSil, productFormMode, selectedProduct?.fotoUrl]);

  useEffect(() => {
    if (!(formData.foto instanceof File) || !urunFotoPreviewUrl?.startsWith('blob:')) {
      return undefined;
    }
    return () => URL.revokeObjectURL(urunFotoPreviewUrl);
  }, [formData.foto, urunFotoPreviewUrl]);

  const parcaFotoPreviewUrl = useMemo(() => {
    if (itemFormData.foto instanceof File) {
      return URL.createObjectURL(itemFormData.foto);
    }
    if (itemFormMode === 'edit' && selectedItem?.fotoUrl && !itemFormData.fotoSil) {
      return getParcaFotoUrl(selectedItem.fotoUrl);
    }
    return null;
  }, [itemFormData.foto, itemFormData.fotoSil, itemFormMode, selectedItem?.fotoUrl]);

  useEffect(() => {
    if (!(itemFormData.foto instanceof File) || !parcaFotoPreviewUrl?.startsWith('blob:')) {
      return undefined;
    }
    return () => URL.revokeObjectURL(parcaFotoPreviewUrl);
  }, [itemFormData.foto, parcaFotoPreviewUrl]);

  const bulkFotoPreviewUrl = useMemo(() => {
    if (bulkFotoFile instanceof File) {
      return URL.createObjectURL(bulkFotoFile);
    }
    return null;
  }, [bulkFotoFile]);

  useEffect(() => {
    if (!(bulkFotoFile instanceof File) || !bulkFotoPreviewUrl?.startsWith('blob:')) {
      return undefined;
    }
    return () => URL.revokeObjectURL(bulkFotoPreviewUrl);
  }, [bulkFotoFile, bulkFotoPreviewUrl]);

  const closeBulkFotoModal = useCallback(() => {
    if (bulkFotoInProgress) return;
    setBulkFotoModal(null);
    setBulkFotoFile(null);
  }, [bulkFotoInProgress]);

  const closeBulkFiyatModal = useCallback(() => {
    if (bulkFiyatInProgress) return;
    setBulkFiyatModal(null);
  }, [bulkFiyatInProgress]);

  // Ana ürün arama için
  const [mainProductSearchQuery, setMainProductSearchQuery] = useState('');
  const [mainProducts, setMainProducts] = useState([]);
  const [mainProductSearchLoading, setMainProductSearchLoading] = useState(false);

  const [browseContentsTick, setBrowseContentsTick] = useState(0);

  const isItemUrunMode =
    (activeTab === 'parca' || activeTab === 'ariza') && itemListeModu === 'urun';
  const categoryTipForAccordion = isItemUrunMode ? 'urun' : activeTab;

  const pickDefaultBirimId = useCallback((list) => {
    const act = (list || []).filter((b) => b.aktif !== false);
    const adet = act.find((b) => b.kod === 'ADET');
    const id = adet ? adet.id || adet._id : act[0]?.id || act[0]?._id;
    return id ? String(id) : '';
  }, []);

  useEffect(() => {
    if (activeTab !== 'parca') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchBirimler();
        const arr = r?.veri?.birimler || [];
        if (!cancelled) setBirimler(Array.isArray(arr) ? arr : []);
      } catch {
        if (!cancelled) setBirimler([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'urun') {
      setUrunChecklistSecenekleri([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchChecklists();
        if (!cancelled && r?.basarili && Array.isArray(r.veri)) {
          setUrunChecklistSecenekleri(r.veri);
        } else if (!cancelled) setUrunChecklistSecenekleri([]);
      } catch {
        if (!cancelled) setUrunChecklistSecenekleri([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await fetchCategories({
        kategoriTipi: categoryTipForAccordion,
        ustKategoriId: 'root',
      });
      const list = response?.veri?.kategoriler || response?.veri || [];
      setCategories(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('[loadCategories] Error:', err);
      setCategories([]);
    }
  }, [activeTab, categoryTipForAccordion]);

  const loadProducts = useCallback(async () => {
    if (activeTab !== 'urun') {
      setProducts([]);
      setSearchLoading(false);
      return;
    }

    const q = debouncedListSearch.trim();
    if (q.length < 3) {
      setProducts([]);
      setSearchLoading(false);
      return;
    }

    const fetchGen = ++accordionListFetchGenRef.current;

    setLoading(true);
    setSearchLoading(true);
    setError('');
    try {
      const activeStatus = activeSubTab === 'ana-urunler' ? true : activeSubTab === 'pasif-urunler' ? false : undefined;

      const fetchPage = async (page, limit) =>
        searchContentItems(q, {
          icerikTipi: activeTab,
          aktif: activeStatus,
          sayfa: page,
          limit,
        });

      const aggregated = await fetchAllIcerikForAccordion(fetchPage);
      if (fetchGen !== accordionListFetchGenRef.current) {
        return;
      }
      setContentsByCategoryId({});
      contentsLoadedForCategoryRef.current = new Set();
      setProducts(aggregated);
      setPaginationMeta({
        total: aggregated.length,
        totalPages: 1,
        currentPage: 1,
      });
    } catch (err) {
      console.error('[loadProducts] Error:', err);
      if (fetchGen === accordionListFetchGenRef.current) {
        setError(getErrorMessage(err, 'Ürünler yüklenemedi'));
        setProducts([]);
        setPaginationMeta({
          total: 0,
          totalPages: 1,
          currentPage: 1,
        });
      }
    } finally {
      if (fetchGen === accordionListFetchGenRef.current) {
        setLoading(false);
        setSearchLoading(false);
      }
    }
  }, [activeTab, activeSubTab, debouncedListSearch]);

  const loadItems = useCallback(async () => {
    if (activeTab === 'urun') {
      setItems([]);
      setSearchLoading(false);
      return;
    }

    const q = debouncedListSearch.trim();
    if (q.length < 3) {
      setItems([]);
      setSearchLoading(false);
      return;
    }

    const fetchGen = ++accordionListFetchGenRef.current;

    setLoading(true);
    setSearchLoading(true);
    setError('');
    try {
      const activeStatus = activeItemSubTab === 'aktif' ? true : activeItemSubTab === 'pasif' ? false : undefined;

      const fetchPage = async (page, limit) =>
        searchContentItems(q, {
          icerikTipi: activeTab,
          aktif: activeStatus,
          sayfa: page,
          limit,
        });

      const aggregated = await fetchAllIcerikForAccordion(fetchPage);
      if (fetchGen !== accordionListFetchGenRef.current) {
        return;
      }
      setContentsByCategoryId({});
      contentsLoadedForCategoryRef.current = new Set();
      setItems(aggregated);
      setPaginationMeta({
        total: aggregated.length,
        totalPages: 1,
        currentPage: 1,
      });
    } catch (err) {
      console.error('[loadItems] Error:', err);
      const tabLabel = activeTab === 'parca' ? 'Parça' : activeTab === 'islem' ? 'İşlem' : activeTab === 'ariza' ? 'Arıza' : 'Kayıt';
      if (fetchGen === accordionListFetchGenRef.current) {
        setError(getErrorMessage(err, `${tabLabel} yüklenemedi`));
        setItems([]);
        setPaginationMeta({
          total: 0,
          totalPages: 1,
          currentPage: 1,
        });
      }
    } finally {
      if (fetchGen === accordionListFetchGenRef.current) {
        setLoading(false);
        setSearchLoading(false);
      }
    }
  }, [activeTab, activeItemSubTab, debouncedListSearch]);

  const loadContentsForLeafCategory = useCallback(
    async (categoryIdStr, { force = false } = {}) => {
      if (!categoryIdStr) return;
      if (categoryIdStr === '__none__') return;
      if (force) {
        contentsLoadedForCategoryRef.current.delete(categoryIdStr);
      }
      if (!force && contentsLoadedForCategoryRef.current.has(categoryIdStr)) return;

      setLeafContentsLoadingIds((prev) => new Set(prev).add(categoryIdStr));
      try {
        const activeStatusUrun =
          activeSubTab === 'ana-urunler' ? true : activeSubTab === 'pasif-urunler' ? false : undefined;
        const activeStatusItem =
          activeItemSubTab === 'aktif' ? true : activeItemSubTab === 'pasif' ? false : undefined;

        const listeTipi =
          (activeTab === 'parca' || activeTab === 'ariza') && itemListeModu === 'urun'
            ? 'urun'
            : activeTab;

        const fetchPage = async (page, limit) =>
          fetchProducts({
            icerikTipi: listeTipi,
            aktif: activeTab === 'urun' ? activeStatusUrun : activeStatusItem,
            kategoriId: categoryIdStr,
            sayfa: page,
            limit,
          });

        const aggregated = await fetchAllIcerikForAccordion(fetchPage);
        setContentsByCategoryId((prev) => ({ ...prev, [categoryIdStr]: aggregated }));
        contentsLoadedForCategoryRef.current.add(categoryIdStr);
      } catch (err) {
        console.error('[loadContentsForLeafCategory]', err);
        if (window.showNotification) {
          window.showNotification('error', getErrorMessage(err, 'İçerikler yüklenemedi'));
        }
      } finally {
        setLeafContentsLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(categoryIdStr);
          return next;
        });
      }
    },
    [activeTab, activeSubTab, activeItemSubTab, itemListeModu],
  );

  const loadLinkedPartsForProduct = useCallback(
    async (urunIcerikIdStr) => {
      if (!urunIcerikIdStr) return;
      setLinkedPartsLoadingIds((prev) => new Set(prev).add(urunIcerikIdStr));
      try {
        const activeStatus =
          activeItemSubTab === 'aktif' ? true : activeItemSubTab === 'pasif' ? false : undefined;
        const fetchPage = async (page, limit) =>
          fetchProducts({
            icerikTipi: activeTab,
            aktif: activeStatus,
            eslesenUrunIcerikId: urunIcerikIdStr,
            sayfa: page,
            limit,
          });
        const agg = await fetchAllIcerikForAccordion(fetchPage);
        setLinkedPartsByProductId((prev) => ({ ...prev, [urunIcerikIdStr]: agg }));
      } catch (err) {
        console.error('[loadLinkedPartsForProduct]', err);
        if (window.showNotification) {
          window.showNotification('error', getErrorMessage(err, 'Parçalar yüklenemedi'));
        }
      } finally {
        setLinkedPartsLoadingIds((prev) => {
          const n = new Set(prev);
          n.delete(urunIcerikIdStr);
          return n;
        });
      }
    },
    [activeTab, activeItemSubTab],
  );

  const handleBagliUrunChange = useCallback(
    async (rows) => {
      const ozet = (rows || []).map((r) => ({
        id: String(r.id),
        ad: r.ad,
        kod: r.kod,
      }));

      let previousOzet = [];
      setItemFormData((prev) => {
        previousOzet = prev.bagliUrunOzet || [];
        return { ...prev, bagliUrunOzet: ozet };
      });

      if (itemFormMode !== 'edit' || !selectedItem) return;

      const itemId = selectedItem.id || selectedItem._id;
      if (!itemId) return;

      const seq = ++bagliUrunPersistSeqRef.current;
      setBagliUrunSaving(true);
      try {
        const response = await updateProduct(itemId, {
          icerikTipi: activeTab,
          bagliUrunIcerikIdleri: ozet.map((r) => r.id),
        });
        if (seq !== bagliUrunPersistSeqRef.current) return;
        if (!response?.basarili) {
          throw new Error(response?.mesaj || response?.hata || 'Ürün bağlantıları kaydedilemedi');
        }

        const prevIds = new Set((previousOzet || []).map((r) => r.id));
        const nextIds = new Set(ozet.map((r) => r.id));
        const affected = new Set([...prevIds, ...nextIds]);
        await Promise.all([...affected].map((id) => loadLinkedPartsForProduct(id)));

        setSelectedItem((prev) =>
          prev ? { ...prev, bagliUrunIcerikIdleri: ozet.map((r) => r.id) } : prev,
        );
      } catch (err) {
        if (seq !== bagliUrunPersistSeqRef.current) return;
        setItemFormData((prev) => ({ ...prev, bagliUrunOzet: previousOzet }));
        if (isUrunPickerModalOpen) {
          setUrunPickerSyncKey((k) => k + 1);
        }
        if (window.showNotification) {
          window.showNotification('error', getErrorMessage(err, 'Ürün bağlantıları kaydedilemedi'));
        }
      } finally {
        if (seq === bagliUrunPersistSeqRef.current) {
          setBagliUrunSaving(false);
        }
      }
    },
    [itemFormMode, selectedItem, activeTab, loadLinkedPartsForProduct, isUrunPickerModalOpen],
  );

  const reloadAccordionData = useCallback(() => {
    const len = debouncedListSearch.trim().length;
    if (len >= 3) {
      if (activeTab === 'urun') loadProducts();
      else if (activeTab === 'parca' || activeTab === 'islem' || activeTab === 'ariza') loadItems();
    } else if (len === 0) {
      setContentsByCategoryId({});
      contentsLoadedForCategoryRef.current = new Set();
      setBrowseContentsTick((t) => t + 1);
    }
  }, [debouncedListSearch, activeTab, loadProducts, loadItems]);

  const excelEditExportEnabled =
    activeTab === 'urun' || activeTab === 'parca' || activeTab === 'islem';

  const resolveAktifFilterForExcel = useCallback(() => {
    if (activeTab === 'urun') {
      return activeSubTab === 'ana-urunler' ? true : activeSubTab === 'pasif-urunler' ? false : undefined;
    }
    return activeItemSubTab === 'aktif' ? true : activeItemSubTab === 'pasif' ? false : undefined;
  }, [activeTab, activeSubTab, activeItemSubTab]);

  const fetchCategoryContentsForExcel = useCallback(
    async (categoryIdStr) => {
      const cached = contentsByCategoryId[categoryIdStr];
      if (Array.isArray(cached) && cached.length > 0) return cached;
      const aktif = resolveAktifFilterForExcel();
      const filters = { icerikTipi: activeTab, kategoriId: categoryIdStr };
      if (aktif !== undefined) filters.aktif = aktif;
      return fetchAllContentsByPaginating(filters);
    },
    [activeTab, contentsByCategoryId, resolveAktifFilterForExcel],
  );

  const handleExcelExportCategory = useCallback(
    async (category) => {
      if (!excelEditExportEnabled) return;
      const catId = String(category.id || category._id);
      try {
        const items = await fetchCategoryContentsForExcel(catId);
        if (!items.length) {
          if (window.showNotification) window.showNotification('warning', 'Dışa aktarılacak kayıt yok.');
          return;
        }
        downloadIcerikEditExcel(activeTab, { type: 'kategori', kategoriId: catId }, items);
        if (window.showNotification) {
          window.showNotification('success', 'Excel dosyası indirildi.');
        }
      } catch (err) {
        if (window.showNotification) {
          window.showNotification('error', err?.message || 'Dışa aktarma başarısız');
        }
      }
    },
    [activeTab, excelEditExportEnabled, fetchCategoryContentsForExcel],
  );

  const handleExcelEditCategory = useCallback(
    (category) => {
      if (!excelEditExportEnabled) return;
      const catId = String(category.id || category._id);
      setExcelEditContext({
        contentType: activeTab,
        scope: {
          type: 'kategori',
          kategoriId: catId,
          label: category.kategoriAdi || '',
        },
      });
    },
    [activeTab, excelEditExportEnabled],
  );

  const handleExcelExportUrunParca = useCallback(async () => {
    const p = selectedProductForParts;
    const pid = p?.id || p?._id;
    if (!pid) return;
    const pidStr = String(pid);
    try {
      const cached = linkedPartsByProductId[pidStr];
      let items = Array.isArray(cached) && cached.length > 0 ? cached : null;
      if (!items) {
        const aktif = resolveAktifFilterForExcel();
        const filters = { icerikTipi: 'parca', eslesenUrunIcerikId: pidStr };
        if (aktif !== undefined) filters.aktif = aktif;
        items = await fetchAllContentsByPaginating(filters);
      }
      if (!items.length) {
        if (window.showNotification) window.showNotification('warning', 'Dışa aktarılacak parça yok.');
        return;
      }
      downloadIcerikEditExcel('parca', { type: 'urun', urunIcerikId: pidStr }, items);
      if (window.showNotification) window.showNotification('success', 'Excel dosyası indirildi.');
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', err?.message || 'Dışa aktarma başarısız');
      }
    }
  }, [linkedPartsByProductId, resolveAktifFilterForExcel, selectedProductForParts]);

  const toggleUrunTreeCollapsed = useCallback(() => {
    setUrunTreeCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('hlm-ctp-urun-tree-collapsed', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const toggleUrunLinkedCollapsed = useCallback(() => {
    setUrunLinkedCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('hlm-ctp-urun-linked-collapsed', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const handleExcelEditUrunParca = useCallback(() => {
    const p = selectedProductForParts;
    const pid = p?.id || p?._id;
    if (!pid) return;
    setExcelEditContext({
      contentType: 'parca',
      scope: {
        type: 'urun',
        urunIcerikId: String(pid),
        label: `${getContentCode(p)} — ${getContentName(p)}`,
      },
    });
  }, [selectedProductForParts]);

  // Akordeon liste API’si: hızlı yazımda her tuşta istek atılıp 1–2 harfte tam liste + yarış riski oluşmasın
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedListSearch(searchInput.trim());
    }, 320);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Kategorileri yükle - sadece aktif sekme değiştiğinde
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchSetting('ISKONTO_ORANI');
        if (cancelled) return;
        if (r?.basarili && r?.veri?.setting?.deger != null && r.veri.setting.deger !== '') {
          const d = parseFloat(r.veri.setting.deger);
          setIskontoOrani(Number.isFinite(d) ? d : 0);
        } else {
          setIskontoOrani(0);
        }
      } catch {
        if (!cancelled) setIskontoOrani(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (activeTab === 'islem' && gorunumFromUrl === 'urun') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('gorunum');
        return next;
      }, { replace: true });
    }
  }, [activeTab, gorunumFromUrl, setSearchParams]);

  useEffect(() => {
    setLinkedPartsByProductId({});
    setSelectedProductForParts(null);
    setExcelImportUrunContext(null);
    setUrunTreeSearchInput('');
    setDebouncedUrunTreeSearch('');
    setUrunTreeSearchResults(null);
    setExpandedCategoryIds(new Set());
    setUncategorizedExpanded(false);
  }, [itemListeModu, activeItemSubTab, activeTab, activeSubTab]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedUrunTreeSearch(urunTreeSearchInput.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [urunTreeSearchInput]);

  useEffect(() => {
    if (!isItemUrunMode || debouncedUrunTreeSearch.length < 3) {
      setUrunTreeSearchResults(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await searchContentItems(debouncedUrunTreeSearch, {
          icerikTipi: 'urun',
          aktif: activeSubTab === 'ana-urunler',
          limit: 100,
        });
        if (cancelled) return;
        const list = res?.veri?.urunler || res?.veri?.icerikler || [];
        setUrunTreeSearchResults(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setUrunTreeSearchResults(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isItemUrunMode, debouncedUrunTreeSearch, activeSubTab]);

  useEffect(() => {
    const q = debouncedListSearch.trim();
    const len = q.length;

    if (activeTab === 'urun') {
      setItems([]);
      if (len >= 3) {
        loadProducts();
      } else if (len === 0) {
        setProducts([]);
        setContentsByCategoryId({});
        contentsLoadedForCategoryRef.current = new Set();
        fetchedCategoryChildrenRef.current = new Set();
        setConfirmedLeafCategoryIds(new Set());
        confirmedLeafRef.current = new Set();
        setPaginationMeta({ total: 0, totalPages: 1, currentPage: 1 });
        loadCategories();
        setBrowseContentsTick((t) => t + 1);
      } else {
        setProducts([]);
      }
      return;
    }

    if (activeTab === 'parca' || activeTab === 'islem' || activeTab === 'ariza') {
      setProducts([]);
      if (len >= 3) {
        loadItems();
      } else if (len === 0) {
        setItems([]);
        setContentsByCategoryId({});
        contentsLoadedForCategoryRef.current = new Set();
        fetchedCategoryChildrenRef.current = new Set();
        setConfirmedLeafCategoryIds(new Set());
        confirmedLeafRef.current = new Set();
        setPaginationMeta({ total: 0, totalPages: 1, currentPage: 1 });
        loadCategories();
        setBrowseContentsTick((t) => t + 1);
      } else {
        setItems([]);
      }
      return;
    }

    setProducts([]);
    setItems([]);
  }, [
    activeTab,
    activeSubTab,
    activeItemSubTab,
    debouncedListSearch,
    loadProducts,
    loadItems,
    loadCategories,
    itemListeModu,
  ]);

  const isAccordionSearchActive = debouncedListSearch.trim().length >= 3;

  const showItemUrunSearchFlatList = isItemUrunMode && isAccordionSearchActive;

  /** Debounce beklerken veya liste isteği sürerken arama kutusu spinner (400ms sonrası “takılma” yok) */
  const showAccordionSearchBoxSpinner = useMemo(() => {
    const t = searchInput.trim();
    if (t.length < 3) return false;
    return t !== debouncedListSearch.trim() || searchLoading;
  }, [searchInput, debouncedListSearch, searchLoading]);

  const contentListForAccordion = useMemo(() => {
    if (activeTab === 'urun') {
      return isAccordionSearchActive ? products : Object.values(contentsByCategoryId).flat();
    }
    if (activeTab === 'parca' || activeTab === 'islem' || activeTab === 'ariza') {
      return isAccordionSearchActive ? items : Object.values(contentsByCategoryId).flat();
    }
    return [];
  }, [activeTab, products, items, contentsByCategoryId, isAccordionSearchActive]);

  const itemsByCategoryId = useMemo(() => {
    const m = new Map();
    if (isAccordionSearchActive) {
      const list = activeTab === 'urun' ? products : items;
      list.forEach((row) => {
        const kid = row.kategoriId;
        const key = kid == null || kid === '' ? '__none__' : String(kid);
        if (!m.has(key)) m.set(key, []);
        const bucket = m.get(key);
        const rid = row.id != null && row.id !== '' ? String(row.id) : row._id != null ? String(row._id) : '';
        if (rid && bucket.some((r) => String(r.id ?? r._id) === rid)) {
          return;
        }
        bucket.push(row);
      });
    } else {
      Object.entries(contentsByCategoryId).forEach(([key, rows]) => {
        if (!Array.isArray(rows)) return;
        m.set(key, rows);
      });
    }
    const sorted = new Map();
    m.forEach((rows, key) => {
      sorted.set(key, sortContentsRows(rows, listSort));
    });
    return sorted;
  }, [activeTab, products, items, contentsByCategoryId, isAccordionSearchActive, listSort]);

  const findContentItemById = useCallback(
    (idStr) => {
      const target = String(idStr || '');
      if (!target) return null;
      for (const rows of itemsByCategoryId.values()) {
        const found = (rows || []).find((r) => String(r.id ?? r._id) === target);
        if (found) return found;
      }
      return null;
    },
    [itemsByCategoryId],
  );

  const contentAccordionCategoryTree = useMemo(() => {
    const full = buildCategoryTree(categories, listSort);
    if (!isAccordionSearchActive) return full;
    return filterCategoryTreeWithVisibleContent(full, itemsByCategoryId);
  }, [categories, itemsByCategoryId, isAccordionSearchActive, listSort]);

  /** Ürün ağacı arama (3+ karakter) sonuçları — listSort ile */
  const sortedUrunTreeSearchResults = useMemo(() => {
    if (!Array.isArray(urunTreeSearchResults)) return urunTreeSearchResults;
    return sortContentsRows(urunTreeSearchResults, listSort);
  }, [urunTreeSearchResults, listSort]);

  /** Parça/ariza + ürün bazlı + düz arama listesi */
  const sortedItemUrunFlatSearchItems = useMemo(
    () => sortContentsRows(items, listSort),
    [items, listSort],
  );

  const isUrunTreeSearchActive = isItemUrunMode && debouncedUrunTreeSearch.length >= 3 && Array.isArray(urunTreeSearchResults);

  const parentByChildId = useMemo(() => {
    const m = new Map();
    categories.forEach((c) => {
      const id = String(c.id || c._id);
      const p = c.ustKategoriId;
      m.set(id, p != null && p !== '' ? String(p) : null);
    });
    return m;
  }, [categories]);

  useEffect(() => {
    confirmedLeafRef.current = confirmedLeafCategoryIds;
  }, [confirmedLeafCategoryIds]);

  useEffect(() => {
    setContentBulkSelectCategoryId(null);
    setContentBulkSelectedIds(new Set());
  }, [activeTab, isItemUrunMode, isAktifFromUrl]);

  const contentBulkSelectCount = contentBulkSelectedIds.size;

  const toggleContentBulkSelectMode = useCallback((categoryIdStr, e) => {
    e?.stopPropagation?.();
    setContentBulkSelectCategoryId((prev) => {
      if (prev === categoryIdStr) {
        setContentBulkSelectedIds(new Set());
        return null;
      }
      setContentBulkSelectedIds(new Set());
      return categoryIdStr;
    });
  }, []);

  const toggleContentBulkItem = useCallback((idStr, checked) => {
    setContentBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(idStr);
      else next.delete(idStr);
      return next;
    });
  }, []);

  const toggleContentBulkSelectAllInCategory = useCallback((leafRows, checked) => {
    setContentBulkSelectedIds((prev) => {
      const next = new Set(prev);
      (leafRows || []).forEach((item) => {
        const idStr = String(item.id || item._id);
        if (checked) next.add(idStr);
        else next.delete(idStr);
      });
      return next;
    });
  }, []);

  const clearContentBulkSelection = useCallback(() => {
    setContentBulkSelectedIds(new Set());
    setContentBulkSelectCategoryId(null);
  }, []);

  const handleBulkSetContentActive = useCallback(
    async (ids, makeActive) => {
      const list = Array.isArray(ids) ? ids.map(String).filter(Boolean) : [];
      if (list.length === 0) return;
      const tipLabelMap = { urun: 'ürün', parca: 'parça', islem: 'işlem', ariza: 'arıza' };
      const tipLabel = tipLabelMap[activeTab] || 'kayıt';
      const ok = await confirm({
        title: makeActive ? 'Seçilenleri aktif yap' : 'Seçilenleri pasif yap',
        message: `${list.length} ${tipLabel} ${makeActive ? 'aktif' : 'pasif'} yapılacak. Devam?`,
        variant: makeActive ? 'info' : 'warning',
        confirmLabel: 'Evet',
      });
      if (!ok) return;
      setBulkMoveInProgress(true);
      try {
        for (const idStr of list) {
          const res = makeActive ? await activateProduct(idStr) : await deactivateProduct(idStr);
          if (!res?.basarili) {
            throw new Error(
              res?.mesaj || res?.hata || (makeActive ? 'Aktif yapılamadı' : 'Pasif yapılamadı'),
            );
          }
        }
        if (window.showNotification) {
          window.showNotification(
            'success',
            `${list.length} ${tipLabel} ${makeActive ? 'aktif' : 'pasif'} yapıldı.`,
          );
        }
        clearContentBulkSelection();
        reloadAccordionData();
      } catch (err) {
        console.error('[handleBulkSetContentActive]', err);
        if (window.showNotification) {
          window.showNotification(
            'error',
            getErrorMessage(err, makeActive ? 'Toplu aktifleştirme tamamlanamadı' : 'Toplu pasifleştirme tamamlanamadı'),
          );
        }
        reloadAccordionData();
      } finally {
        setBulkMoveInProgress(false);
      }
    },
    [activeTab, clearContentBulkSelection, confirm, reloadAccordionData],
  );

  const renderContentBulkSelectControls = useCallback(
    (categoryIdStr) => {
      if (!supportsContentBulkSelect || isItemUrunMode || isAccordionSearchActive) return null;
      const isActive = contentBulkSelectCategoryId === categoryIdStr;
      const count = isActive ? contentBulkSelectCount : 0;

      return (
        <span
          className={`ctp-content-bulk-select${isActive ? ' ctp-content-bulk-select--active' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="ctp-content-bulk-select__mode">
            <span className="ctp-content-bulk-select__mode-label">Seçim modu</span>
            <button
              type="button"
              className={`ctp-toggle-switch ctp-content-bulk-select__switch${isActive ? ' ctp-toggle-switch--active' : ''}`}
              role="switch"
              aria-checked={isActive}
              aria-label="Seçim modu"
              onClick={(e) => toggleContentBulkSelectMode(categoryIdStr, e)}
            >
              <span className="ctp-toggle-switch__knob" />
            </button>
          </span>
          {isActive && (
            <>
              <span className="ctp-content-bulk-select__count" aria-live="polite">
                {count} seçildi
              </span>
              {isAktifFromUrl ? (
                <button
                  type="button"
                  className="ctp-content-bulk-select__status-btn ctp-content-bulk-select__status-btn--deactivate"
                  disabled={count === 0 || bulkMoveInProgress || bulkFotoInProgress || bulkFiyatInProgress}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBulkSetContentActive(Array.from(contentBulkSelectedIds), false);
                  }}
                >
                  Seçilenleri pasif yap
                </button>
              ) : (
                <button
                  type="button"
                  className="ctp-content-bulk-select__status-btn ctp-content-bulk-select__status-btn--activate"
                  disabled={count === 0 || bulkMoveInProgress || bulkFotoInProgress || bulkFiyatInProgress}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBulkSetContentActive(Array.from(contentBulkSelectedIds), true);
                  }}
                >
                  Seçilenleri aktif yap
                </button>
              )}
              <button
                type="button"
                className="ctp-content-bulk-select__move-btn"
                disabled={count === 0 || bulkMoveInProgress || bulkFotoInProgress || bulkFiyatInProgress}
                onClick={(e) => {
                  e.stopPropagation();
                  setMoveTargetModal({
                    kind: 'icerik-bulk',
                    ids: Array.from(contentBulkSelectedIds),
                    sourceCategoryId: categoryIdStr,
                  });
                }}
              >
                Seçilenleri taşı
              </button>
              {activeTab === 'parca' && (
                <>
                  <button
                    type="button"
                    className="ctp-content-bulk-select__foto-btn"
                    disabled={count === 0 || bulkMoveInProgress || bulkFotoInProgress || bulkFiyatInProgress}
                    onClick={(e) => {
                      e.stopPropagation();
                      setBulkFotoFile(null);
                      setBulkFotoModal({ ids: Array.from(contentBulkSelectedIds) });
                    }}
                  >
                    Seçilenlere foto ekle
                  </button>
                  <button
                    type="button"
                    className="ctp-content-bulk-select__fiyat-btn"
                    disabled={count === 0 || bulkMoveInProgress || bulkFotoInProgress || bulkFiyatInProgress}
                    onClick={(e) => {
                      e.stopPropagation();
                      setBulkFiyatModal({ ids: Array.from(contentBulkSelectedIds) });
                    }}
                  >
                    Seçilenlere fiyat ekle/düzenle
                  </button>
                </>
              )}
            </>
          )}
        </span>
      );
    },
    [
      supportsContentBulkSelect,
      isItemUrunMode,
      isAccordionSearchActive,
      contentBulkSelectCategoryId,
      contentBulkSelectCount,
      contentBulkSelectedIds,
      bulkMoveInProgress,
      bulkFotoInProgress,
      bulkFiyatInProgress,
      isAktifFromUrl,
      activeTab,
      toggleContentBulkSelectMode,
      handleBulkSetContentActive,
    ],
  );

  useEffect(() => {
    if (!isAccordionSearchActive) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchCategories({ kategoriTipi: categoryTipForAccordion });
        const list = response?.veri?.kategoriler || response?.veri || [];
        if (!cancelled) setCategories(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('[categoriesFullTreeForSearch]', err);
        if (!cancelled) setCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAccordionSearchActive, categoryTipForAccordion]);

  useEffect(() => {
    if (isAccordionSearchActive) return;
    if (debouncedListSearch.trim().length !== 0) return;
    let cancelled = false;
    (async () => {
      try {
        const activeStatusUrun =
          activeSubTab === 'ana-urunler' ? true : activeSubTab === 'pasif-urunler' ? false : undefined;
        const activeStatusItem =
          activeItemSubTab === 'aktif' ? true : activeItemSubTab === 'pasif' ? false : undefined;
        const activeStatus = activeTab === 'urun' ? activeStatusUrun : activeStatusItem;
        const browseIcerikTipi =
          (activeTab === 'parca' || activeTab === 'ariza') && itemListeModu === 'urun'
            ? 'urun'
            : activeTab;
        const fetchPage = async (page, limit) =>
          fetchProducts({
            icerikTipi: browseIcerikTipi,
            aktif: activeStatus,
            sayfa: page,
            limit,
          });
        const allItems = await fetchAllIcerikForAccordion(fetchPage);
        if (cancelled) return;
        const grouped = {};
        allItems.forEach((item) => {
          const kid = item.kategoriId;
          const key = kid != null && kid !== '' ? String(kid) : '__none__';
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(item);
        });
        setContentsByCategoryId(grouped);
        Object.keys(grouped).forEach((key) => contentsLoadedForCategoryRef.current.add(key));
        setPaginationMeta({ total: allItems.length, totalPages: 1, currentPage: 1 });
      } catch (e) {
        console.error('[preloadAllContents]', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    activeSubTab,
    activeItemSubTab,
    itemListeModu,
    isAccordionSearchActive,
    debouncedListSearch,
    browseContentsTick,
  ]);

  useEffect(() => {
    const wasActive = prevSearchActiveRef.current;
    prevSearchActiveRef.current = isAccordionSearchActive;

    if (!isAccordionSearchActive) {
      if (wasActive) {
        setExpandedCategoryIds(new Set());
      }
      return;
    }
    const list = contentListForAccordion;
    const next = new Set();
    list.forEach((item) => {
      const kid = item.kategoriId;
      if (kid == null || kid === '') return;
      const kidStr = String(kid);
      next.add(kidStr);
      let walker = kidStr;
      let guard = 0;
      while (guard++ < 200) {
        const parent = parentByChildId.get(walker);
        if (!parent) break;
        next.add(parent);
        walker = parent;
      }
    });
    setExpandedCategoryIds(next);
  }, [contentListForAccordion, parentByChildId, isAccordionSearchActive]);

  // Category CRUD handlers
  const openCategoryFormForCreate = (parentCategory = null) => {
    setCategoryFormMode('create');
    setSelectedParentCategoryForCreate(parentCategory);
    setSelectedCategoryForEdit(null);
    setCategoryFormData({ kategoriAdi: '', malKodu: '', modelKodu: '' });
    setCategoryFormError('');
    setIsCategoryFormModalOpen(true);
  };

  const openCategoryFormForEdit = (category) => {
    setCategoryFormMode('edit');
    setSelectedCategoryForEdit(category);
    setSelectedParentCategoryForCreate(null);
    setCategoryFormData({
      kategoriAdi: category.kategoriAdi || '',
      malKodu: category.malKodu != null ? String(category.malKodu) : '',
      modelKodu: category.modelKodu != null ? String(category.modelKodu) : '',
    });
    setCategoryFormError('');
    setIsCategoryFormModalOpen(true);
  };

  const closeCategoryFormModal = () => {
    setIsCategoryFormModalOpen(false);
    setSelectedCategoryForEdit(null);
    setSelectedParentCategoryForCreate(null);
    setCategoryFormData({ kategoriAdi: '', malKodu: '', modelKodu: '' });
    setCategoryFormError('');
  };

  const handleCategoryFormSubmit = async (e) => {
    e.preventDefault();
    setCategoryFormError('');
    if (!categoryFormData.kategoriAdi.trim()) {
      setCategoryFormError('Kategori adı zorunludur.');
      return;
    }
    setCategoryFormSubmitting(true);
    try {
      if (categoryFormMode === 'create') {
        const payload = {
          kategoriAdi: categoryFormData.kategoriAdi.trim(),
          kategoriTipi: activeTab,
          malKodu: categoryFormData.malKodu.trim(),
          modelKodu: categoryFormData.modelKodu.trim(),
          ...(selectedParentCategoryForCreate
            ? { ustKategoriId: selectedParentCategoryForCreate.id || selectedParentCategoryForCreate._id }
            : {}),
        };
        await createCategory(payload);
        if (window.showNotification) window.showNotification('success', 'Kategori eklendi.');
      } else {
        const payload = {
          kategoriAdi: categoryFormData.kategoriAdi.trim(),
          malKodu: categoryFormData.malKodu.trim(),
          modelKodu: categoryFormData.modelKodu.trim(),
        };
        const categoryId = selectedCategoryForEdit.id || selectedCategoryForEdit._id;
        await updateCategory(categoryId, payload);
        if (window.showNotification) window.showNotification('success', 'Kategori güncellendi.');
      }
      closeCategoryFormModal();
      fetchedCategoryChildrenRef.current = new Set();
      setConfirmedLeafCategoryIds(new Set());
      confirmedLeafRef.current = new Set();
      await loadCategories();
      reloadAccordionData();
    } catch (err) {
      setCategoryFormError(getErrorMessage(err, 'Kategori kaydedilemedi'));
    } finally {
      setCategoryFormSubmitting(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    const categoryId = category.id || category._id;
    const categoryName = category.kategoriAdi || 'Kategori';
    const ok = await confirm({
      title: 'Kategoriyi sil',
      message: `"${categoryName}" silinecek. Alt kategoriler bir üst gruba bağlanır, bağlı içerikler silinmez. Devam edilsin mi?`,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) return;
    try {
      await deleteCategory(categoryId);
      if (window.showNotification) window.showNotification('success', 'Kategori silindi.');
      fetchedCategoryChildrenRef.current = new Set();
      setConfirmedLeafCategoryIds(new Set());
      confirmedLeafRef.current = new Set();
      await loadCategories();
      reloadAccordionData();
    } catch (err) {
      if (window.showNotification) window.showNotification('error', getErrorMessage(err, 'Kategori silinemedi'));
    }
  };

  // contentType changes cause a full remount via React key, so no explicit reset needed

  const handleSearchChange = (event) => {
    const value = sanitizeSearchInput(event.target.value, { trimEdges: false });
    setSearchInput(value); // searchInput state'ini güncelle (debounce useEffect tetiklenecek)
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setDebouncedListSearch('');
    setSearchLoading(false);
  };
  
  const setAktifToggle = (isAktif) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (isAktif) next.delete('aktif');
      else next.set('aktif', 'false');
      return next;
    }, { replace: true });
  };

  const setGorunumToggle = (mode) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (mode === 'kategori') next.delete('gorunum');
      else next.set('gorunum', mode);
      return next;
    }, { replace: true });
  };

  const setListSortInUrl = (value) => {
    const v = parseListSortParam(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === LIST_SORT_DEFAULT) {
          next.delete('sira');
        } else {
          next.set('sira', v);
        }
        return next;
      },
      { replace: true },
    );
  };

  const openAddContentForLeafCategory = (category) => {
    setCategoryLockedInModal(true);
    const cid = category.id || category._id;
    setSelectedCategory(cid);
    setSelectedCategoryName(category.kategoriAdi || '');
    setFormError('');
    if (activeTab === 'urun') {
      setProductFormMode('create');
      setSelectedProduct(null);
      setFormData({
        icerikAdi: '',
        urunAdi2: '',
        icerikKodu: '',
        aciklama: '',
        kurulumGarantiSuresiAy: '24',
        malKodu: '',
        modelKodu: '',
        checklistId: '',
        foto: null,
        fotoSil: false,
      });
      setIsProductFormModalOpen(true);
    } else {
      setItemFormMode('create');
      setSelectedItem(null);
      setItemFormData({
        ...emptyItemFormFields(),
        birimId: activeTab === 'parca' ? pickDefaultBirimId(birimler) : '',
      });
      setIsItemFormModalOpen(true);
    }
  };


  const handleOpenCategorySelect = async () => {
    setExpandedCategoryIds(new Set());
    setIsCategorySelectModalOpen(true);
    setPickerCategories(null);
    try {
      const res = await fetchCategories({ kategoriTipi: activeTab });
      const list = res?.veri?.kategoriler || res?.veri || [];
      setPickerCategories(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('[handleOpenCategorySelect]', err);
      setPickerCategories([]);
    }
  };

  // Kategori seç
  const handleSelectCategory = (category) => {
    setCategoryLockedInModal(false);
    setSelectedCategory(category.id || category._id);
    setSelectedCategoryName(category.kategoriAdi);
    setIsCategorySelectModalOpen(false);
  };

  // Kategori değiştir
  const handleChangeCategory = () => {
    handleOpenCategorySelect();
  };

  // Ana Ürün Arama
  const handleMainProductSearch = async (query) => {
    setMainProductSearchQuery(query);
    setMainProductSearchLoading(true);
    try {
      if (!query || query.trim().length < 2) {
        setMainProducts([]);
        return;
      }
      
      const response = await searchMainProducts(query);
      setMainProducts(response?.veri?.urunler || response?.veri || []);
    } catch (err) {
      console.error('[handleMainProductSearch] Error:', err);
      setMainProducts([]);
    } finally {
      setMainProductSearchLoading(false);
    }
  };

  // Ana Ürün Seç
  const handleSelectMainProduct = () => {
    setIsMainProductSearchModalOpen(false);
  };

  // Form gönderimi
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    try {
      // Frontend Validation (UX için - güvenlik backend'de)
      // İçerik adı olduğu gibi API'ye gönderilecek, trim yapılmaz
      const icerikAdi = formData.icerikAdi;
      if (!icerikAdi || icerikAdi.trim().length === 0) {
        setFormError('Ürün adı gereklidir.');
        setSubmitting(false);
        return;
      }
      if (icerikAdi.length > 200) {
        setFormError('Ürün adı en fazla 200 karakter olabilir.');
        setSubmitting(false);
        return;
      }

      const urunAdi2Raw = formData.urunAdi2 || '';
      if (urunAdi2Raw.length > 200) {
        setFormError('Ürün adı 2 en fazla 200 karakter olabilir.');
        setSubmitting(false);
        return;
      }

      // İçerik kodu olduğu gibi API'ye gönderilecek, trim yapılmaz
      const icerikKodu = formData.icerikKodu;
      if (!icerikKodu || icerikKodu.trim().length === 0) {
        setFormError('Ürün kodu gereklidir.');
        setSubmitting(false);
        return;
      }
      // Boşluk kontrolü (validation için)
      if (/\s/.test(icerikKodu)) {
        setFormError('Ürün kodu boşluk içeremez.');
        setSubmitting(false);
        return;
      }
      if (icerikKodu.length > 50) {
        setFormError('Ürün kodu en fazla 50 karakter olabilir.');
        setSubmitting(false);
        return;
      }

      const malTr = (formData.malKodu || '').trim();
      const modelTr = (formData.modelKodu || '').trim();
      if (malTr.length > 120 || modelTr.length > 120) {
        setFormError('Mal kodu ve model kodu en fazla 120 karakter olabilir.');
        setSubmitting(false);
        return;
      }

      // İçerik açıklaması olduğu gibi API'ye gönderilecek
      const aciklama = formData.aciklama || '';
      if (aciklama.length > 2000) {
        setFormError('Ürün açıklaması en fazla 2000 karakter olabilir.');
        setSubmitting(false);
        return;
      }

      if (!selectedCategory) {
        setFormError('Kategori seçimi gereklidir.');
        setSubmitting(false);
        return;
      }

      const kgStr = String(formData.kurulumGarantiSuresiAy ?? '').trim();
      if (kgStr !== '') {
        const kgNum = parseInt(kgStr, 10);
        if (Number.isNaN(kgNum) || kgNum < 1 || kgNum > 120) {
          setFormError('Kurulum garanti süresi 1 ile 120 ay arasında bir tam sayı olmalıdır.');
          setSubmitting(false);
          return;
        }
      }

      // API çağrısı (ham veri gönderiliyor, backend temizleyecek)
      if (productFormMode === 'create') {
        const productPayload = {
          icerikTipi: activeTab, // Yeni field adı
          icerikAdi: formData.icerikAdi, // Olduğu gibi gönder, trim yapma
          icerikKodu: formData.icerikKodu, // Olduğu gibi gönder, trim yapma
          aciklama: formData.aciklama || null, // Olduğu gibi gönder
        };
        if (kgStr !== '') productPayload.kurulumGarantiSuresiAy = parseInt(kgStr, 10);
        productPayload.malKodu = (formData.malKodu || '').trim();
        productPayload.modelKodu = (formData.modelKodu || '').trim();
        productPayload.urunAdi2 = urunAdi2Raw.trim() === '' ? null : formData.urunAdi2;
        const clId = (formData.checklistId || '').trim();
        if (clId) productPayload.checklistId = clId;
        if (formData.foto instanceof File) {
          productPayload.foto = formData.foto;
        }

        // Kategori seçimi zorunlu
        if (!selectedCategory) {
          throw new Error('Kategori seçimi gereklidir.');
        }
        productPayload.kategoriId = selectedCategory;
        
        console.log('[handleFormSubmit] Creating content payload:', JSON.stringify(productPayload, null, 2));
        console.log('[handleFormSubmit] icerikKodu raw value:', formData.icerikKodu);
        console.log('[handleFormSubmit] icerikKodu char codes:', formData.icerikKodu ? Array.from(formData.icerikKodu).map(c => c.charCodeAt(0)) : null);
        
        const response = await createProduct(productPayload);
        
        if (!response?.basarili) {
          const errorMsg = response?.mesaj || response?.hata || 'Ürün oluşturulamadı';
          throw new Error(errorMsg);
        }
      } else {
        const updatePayload = {
          icerikTipi: 'urun',
          icerikAdi: formData.icerikAdi, // Olduğu gibi gönder, trim yapma
          icerikKodu: formData.icerikKodu, // Olduğu gibi gönder, trim yapma
          aciklama: formData.aciklama || null, // Olduğu gibi gönder
          kurulumGarantiSuresiAy: kgStr === '' ? null : parseInt(kgStr, 10),
          malKodu: (formData.malKodu || '').trim(),
          modelKodu: (formData.modelKodu || '').trim(),
          urunAdi2: urunAdi2Raw.trim() === '' ? null : formData.urunAdi2,
          checklistId: (formData.checklistId || '').trim() || null,
        };
        if (formData.fotoSil) updatePayload.fotoSil = true;
        if (formData.foto instanceof File) updatePayload.foto = formData.foto;
        
        if (!selectedProduct) {
          throw new Error('Düzenlenecek ürün bulunamadı.');
        }
        
        const productId = selectedProduct.id || selectedProduct._id;
        if (!productId) {
          throw new Error('Ürün ID bulunamadı.');
        }
        
        console.log('[handleFormSubmit] Updating product:', productId, updatePayload);
        
        const response = await updateProduct(productId, updatePayload);
        
        if (!response?.basarili) {
          const errorMsg = response?.mesaj || response?.hata || 'Ürün güncellenemedi';
          throw new Error(errorMsg);
        }
      }

      // Başarılı
      if (window.showNotification) {
        window.showNotification('success', productFormMode === 'create' ? 'Ürün başarıyla eklendi.' : 'Ürün başarıyla güncellendi.');
      }

      // Modal'ları kapat ve listeyi yenile
      setIsProductFormModalOpen(false);
      setProductFormMode('create');
      setSelectedProduct(null);
      setSelectedCategory(null);
      setSelectedCategoryName('');
      setFormData({
        icerikAdi: '',
        urunAdi2: '',
        icerikKodu: '',
        aciklama: '',
        kurulumGarantiSuresiAy: '24',
        malKodu: '',
        modelKodu: '',
        checklistId: '',
        foto: null,
        fotoSil: false,
      });
      setFormError('');
      reloadAccordionData();
    } catch (err) {
      setFormError(getErrorMessage(err, 'Ürün kaydedilemedi'));
    } finally {
      setSubmitting(false);
    }
  };

  // Modal kapatma
  const closeProductFormModal = () => {
    setIsProductFormModalOpen(false);
    setProductFormMode('create');
    setSelectedProduct(null);
    setSelectedCategory(null);
    setSelectedCategoryName('');
    setFormData({
      icerikAdi: '',
      urunAdi2: '',
      icerikKodu: '',
      aciklama: '',
      kurulumGarantiSuresiAy: '24',
      malKodu: '',
      modelKodu: '',
      checklistId: '',
      foto: null,
      fotoSil: false,
    });
    setFormError('');
    setCategoryLockedInModal(false);
  };

  const closeCategorySelectModal = () => {
    setIsCategorySelectModalOpen(false);
    setExpandedCategoryIds(new Set());
    setPickerCategories(null);
  };

  const closeMainProductSearchModal = () => {
    setIsMainProductSearchModalOpen(false);
    setMainProductSearchQuery('');
    setMainProducts([]);
  };

  const closeItemFormModal = () => {
    setIsItemFormModalOpen(false);
    setFormError('');
    setItemFormData(emptyItemFormFields());
    setSelectedCategory(null);
    setSelectedCategoryName('');
    setSelectedItem(null);
    setCategoryLockedInModal(false);
  };

  // Parça/İşlem/Arıza form gönderimi
  const handleItemFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    try {
      const fields = getFieldNames(activeTab);
      
      // Validasyon
      if (!itemFormData.adi || itemFormData.adi.trim().length === 0) {
        setFormError(`${fields.labelAdi} gereklidir.`);
        setSubmitting(false);
        return;
      }
      if (itemFormData.adi.length > 200) {
        setFormError(`${fields.labelAdi} en fazla 200 karakter olabilir.`);
        setSubmitting(false);
        return;
      }

      if (!itemFormData.kodu || itemFormData.kodu.trim().length === 0) {
        setFormError(`${fields.labelKod} gereklidir.`);
        setSubmitting(false);
        return;
      }
      if (/\s/.test(itemFormData.kodu)) {
        setFormError(`${fields.labelKod} boşluk içeremez.`);
        setSubmitting(false);
        return;
      }
      if (itemFormData.kodu.length > 50) {
        setFormError(`${fields.labelKod} en fazla 50 karakter olabilir.`);
        setSubmitting(false);
        return;
      }

      if (itemFormData.aciklamasi && itemFormData.aciklamasi.length > 2000) {
        setFormError(`${fields.labelAciklamasi} en fazla 2000 karakter olabilir.`);
        setSubmitting(false);
        return;
      }

      const bagliUrunIds = (itemFormData.bagliUrunOzet || []).map((r) => r.id).filter(Boolean);
      const kategorisizParcaPayload =
        activeTab === 'parca' && !selectedCategory && bagliUrunIds.length > 0;

      if (!selectedCategory && !kategorisizParcaPayload) {
        setFormError('Kategori seçimi gereklidir.');
        setSubmitting(false);
        return;
      }

      if (activeTab === 'parca' && !itemFormData.birimId) {
        setFormError(
          'Parça için birim seçimi gereklidir. Birim listesi boşsa Ayarlar → Birim sekmesinden birim ekleyin.',
        );
        setSubmitting(false);
        return;
      }

      // Fiyat validasyonu
      if (activeTab === 'parca') {
        const fiyatStr = (itemFormData.fiyat || '').trim();
        if (fiyatStr !== '') {
          const fiyatNum = Number(fiyatStr.replace(',', '.'));
          if (Number.isNaN(fiyatNum) || fiyatNum < 0) {
            setFormError('Brüt fiyat 0 veya daha büyük bir sayı olmalıdır.');
            setSubmitting(false);
            return;
          }
        }
      }
      if (activeTab === 'islem') {
        for (const [field, label] of [
          ['fiyatGarantiDahili', 'Garanti dahili fiyat'],
          ['fiyatGarantiHarici', 'Garanti harici fiyat'],
        ]) {
          const raw = (itemFormData[field] || '').trim();
          if (raw === '') continue;
          const n = Number(raw.replace(',', '.'));
          if (Number.isNaN(n) || n < 0) {
            setFormError(`${label} 0 veya daha büyük bir sayı olmalıdır.`);
            setSubmitting(false);
            return;
          }
        }
      }

      // Payload oluştur (yeni field adlarını kullan)
      const payload = {
        icerikTipi: activeTab,
        kategoriId: kategorisizParcaPayload ? null : selectedCategory,
        icerikAdi: itemFormData.adi,
        icerikKodu: itemFormData.kodu,
        aciklama: itemFormData.aciklamasi || null,
      };

      if (activeTab === 'parca') {
        payload.fiyat = itemFormData.fiyat === '' ? null : itemFormData.fiyat;
      }
      if (activeTab === 'islem') {
        payload.fiyatGarantiDahili = itemFormData.fiyatGarantiDahili === '' ? null : itemFormData.fiyatGarantiDahili;
        payload.fiyatGarantiHarici = itemFormData.fiyatGarantiHarici === '' ? null : itemFormData.fiyatGarantiHarici;
      }
      if (activeTab === 'parca') {
        payload.degisimParcaGarantisi = !!itemFormData.degisimParcaGarantisi;
      }
      if (activeTab === 'parca' && itemFormData.foto instanceof File) {
        payload.foto = itemFormData.foto;
      }
      if (activeTab === 'parca' || activeTab === 'ariza') {
        payload.bagliUrunIcerikIdleri = (itemFormData.bagliUrunOzet || []).map((r) => r.id);
      }
      if (activeTab === 'parca') {
        payload.birimId = itemFormData.birimId;
      }

      if (itemFormMode === 'create') {
        const response = await createProduct(payload);
        
        if (!response?.basarili) {
          const errorMsg = response?.mesaj || response?.hata || `${tabLabels[activeTab]} oluşturulamadı`;
          throw new Error(errorMsg);
        }
        
        if (window.showNotification) {
          window.showNotification('success', `${tabLabels[activeTab]} başarıyla oluşturuldu`);
        }
        
        closeItemFormModal();
        reloadAccordionData();
      } else {
        if (!selectedItem) {
          throw new Error('Düzenlenecek kayıt bulunamadı.');
        }
        
        const itemId = selectedItem.id || selectedItem._id;
        if (!itemId) {
          throw new Error('Kayıt ID bulunamadı.');
        }
        
        const updatePayload = {
          icerikTipi: activeTab,
          icerikAdi: itemFormData.adi,
          icerikKodu: itemFormData.kodu,
          aciklama: itemFormData.aciklamasi || null,
        };

        if (activeTab === 'parca') {
          updatePayload.fiyat = itemFormData.fiyat === '' ? null : itemFormData.fiyat;
        }
        if (activeTab === 'islem') {
          updatePayload.fiyatGarantiDahili = itemFormData.fiyatGarantiDahili === '' ? null : itemFormData.fiyatGarantiDahili;
          updatePayload.fiyatGarantiHarici = itemFormData.fiyatGarantiHarici === '' ? null : itemFormData.fiyatGarantiHarici;
        }
        if (activeTab === 'parca') {
          updatePayload.degisimParcaGarantisi = !!itemFormData.degisimParcaGarantisi;
          if (itemFormData.fotoSil) updatePayload.fotoSil = true;
          if (itemFormData.foto instanceof File) updatePayload.foto = itemFormData.foto;
        }
        if (activeTab === 'parca') {
          updatePayload.birimId = itemFormData.birimId;
        }

        const response = await updateProduct(itemId, updatePayload);
        
        if (!response?.basarili) {
          const errorMsg = response?.mesaj || response?.hata || `${tabLabels[activeTab]} güncellenemedi`;
          throw new Error(errorMsg);
        }
        
        if (window.showNotification) {
          window.showNotification('success', `${tabLabels[activeTab]} başarıyla güncellendi`);
        }
        
        closeItemFormModal();
        reloadAccordionData();
      }
    } catch (err) {
      console.error('[handleItemFormSubmit] Error:', err);
      setFormError(getErrorMessage(err, `${tabLabels[activeTab]} kaydedilemedi`));
    } finally {
      setSubmitting(false);
    }
  };

  // Kategori toggle (accordion)
  const toggleCategory = (categoryId) => {
    const idStr = String(categoryId);
    setExpandedCategoryIds((prev) => {
      const newSet = new Set(prev);
      // Set'teki tüm değerleri string'e çevirerek kontrol et
      const hasId = Array.from(newSet).some((id) => String(id) === idStr);
      if (hasId) {
        // String olarak eşleşeni bul ve sil
        const toDelete = Array.from(newSet).find((id) => String(id) === idStr);
        if (toDelete) newSet.delete(toDelete);
      } else {
        newSet.add(idStr);
      }
      return newSet;
    });
  };

  const handleAccordionBranchToggle = useCallback(
    async (categoryId, isExpandedNow) => {
      const categoryIdStr = String(categoryId);
      if (isAccordionSearchActive) {
        toggleCategory(categoryId);
        return;
      }
      if (isExpandedNow) {
        toggleCategory(categoryId);
        return;
      }
      toggleCategory(categoryId);
      if (!fetchedCategoryChildrenRef.current.has(categoryIdStr)) {
        fetchedCategoryChildrenRef.current.add(categoryIdStr);
        try {
          const res = await fetchCategories({
            kategoriTipi: categoryTipForAccordion,
            ustKategoriId: categoryIdStr,
          });
          const raw = res?.veri?.kategoriler || res?.veri || [];
          const arr = Array.isArray(raw) ? raw : [];
          if (arr.length === 0) {
            setConfirmedLeafCategoryIds((prev) => new Set(prev).add(categoryIdStr));
          } else {
            setCategories((prev) => mergeCategoriesDedup(prev, arr));
          }
          await loadContentsForLeafCategory(categoryIdStr);
        } catch (err) {
          console.error('[handleAccordionBranchToggle]', err);
          fetchedCategoryChildrenRef.current.delete(categoryIdStr);
        }
      } else {
        await loadContentsForLeafCategory(categoryIdStr);
      }
    },
    [categoryTipForAccordion, isAccordionSearchActive, loadContentsForLeafCategory],
  );

  // Kategori tree render
  const renderCategoryTree = (categoryTree, level = 0) => {
    return categoryTree.map((category) => {
      const categoryId = category.id || category._id;
      const categoryIdStr = String(categoryId);
      const isExpanded = Array.from(expandedCategoryIds).some((id) => String(id) === categoryIdStr);
      const hasChildren = category.children && category.children.length > 0;

      const handleToggleClick = (e) => {
        // Tüm event'leri durdur
        e.preventDefault();
        e.stopPropagation();
        if (e.nativeEvent) {
          e.nativeEvent.stopImmediatePropagation();
        }
        
        // Toggle işlemini hemen yap
        toggleCategory(categoryId);
        
        // Event'in başka yerlere yayılmasını engelle
        return false;
      };

      const handleSelectClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.nativeEvent) {
          e.nativeEvent.stopImmediatePropagation();
        }
        handleSelectCategory(category);
        return false;
      };

      // Ok butonuna tıklandığında kategori seç butonunun tetiklenmesini engelle
      const handleToggleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent?.stopImmediatePropagation();
        return false;
      };

      const handleToggleMouseUp = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent?.stopImmediatePropagation();
        return false;
      };

      return (
        <div 
          key={categoryId} 
          className="category-tree-item" 
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => {
            // Eğer ok butonuna tıklandıysa, event'i durdur
            if (e.target.closest('.category-toggle-btn')) {
              e.stopPropagation();
            }
          }}
        >
          <div
            className="category-tree-row"
            style={{ paddingLeft: `${level * 24 + 16}px` }}
            onClick={(e) => {
              // Eğer ok butonuna tıklandıysa, event'i durdur
              if (e.target.closest('.category-toggle-btn')) {
                e.stopPropagation();
                return;
              }
              e.stopPropagation();
            }}
          >
            {hasChildren && (
              <button
                type="button"
                className="category-toggle-btn"
                onClick={handleToggleClick}
                onMouseDown={handleToggleMouseDown}
                onMouseUp={handleToggleMouseUp}
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                }}
              >
                <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
              </button>
            )}
            {!hasChildren && <span className="category-toggle-spacer"></span>}
            <button
              type="button"
              className="category-select-btn"
              onClick={handleSelectClick}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <span>{category.kategoriAdi}</span>
              <i className="fas fa-check"></i>
            </button>
          </div>
          {hasChildren && (
            <div 
              className={`category-children ${isExpanded ? 'expanded' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              {isExpanded && renderCategoryTree(category.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // Ürün düzenleme
  const handleEditProduct = (product) => {
    if (!product) {
      console.error('[handleEditProduct] Product is null or undefined');
      return;
    }

    setCategoryLockedInModal(true);
    setProductFormMode('edit');
    setSelectedProduct(product);
    setSelectedCategory(product.kategoriId || null);
    
    // Kategori adını bul
    const category = categories.find((c) => (c.id || c._id) === product.kategoriId);
    setSelectedCategoryName(category ? category.kategoriAdi : '');
    
    const kg = product.kurulumGarantiSuresiAy;
    setFormData({
      icerikAdi: getContentName(product) || '',
      urunAdi2: product.urunAdi2 != null && String(product.urunAdi2) !== '' ? String(product.urunAdi2) : '',
      icerikKodu: getContentCode(product) || '',
      aciklama: getContentDescription(product) || '',
      kurulumGarantiSuresiAy:
        kg != null && Number.isFinite(Number(kg)) ? String(Number(kg)) : '',
      malKodu: product.malKodu != null ? String(product.malKodu) : '',
      modelKodu: product.modelKodu != null ? String(product.modelKodu) : '',
      checklistId:
        product.checklistId != null && String(product.checklistId).trim() !== ''
          ? String(product.checklistId)
          : '',
      foto: null,
      fotoSil: false,
    });
    setIsProductFormModalOpen(true);
    setFormError('');
  };

  const openEditItemModal = (item) => {
    if (!item) return;
    setCategoryLockedInModal(true);
    setSelectedItem(item);
    setSelectedCategory(item.kategoriId);
    const ids = Array.isArray(item.bagliUrunIcerikIdleri)
      ? item.bagliUrunIcerikIdleri.map((x) => String(x))
      : [];
    const bagliUrunOzet = ids.map((id) => ({ id, ad: id, kod: '—' }));
    const rawBirim =
      item.birimId != null
        ? typeof item.birimId === 'object'
          ? item.birimId._id || item.birimId
          : item.birimId
        : item.birim && (item.birim._id || item.birim.id);
    const birimIdStr =
      rawBirim != null && rawBirim !== '' ? String(rawBirim) : pickDefaultBirimId(birimler);
    setItemFormData({
      adi: getContentName(item) || '',
      kodu: getContentCode(item) || '',
      aciklamasi: getContentDescription(item) || '',
      fiyat: item.fiyat !== undefined && item.fiyat !== null ? String(item.fiyat) : '',
      fiyatgaranti: '',
      fiyatGarantiDahili:
        item.fiyatGarantiDahili != null
          ? String(item.fiyatGarantiDahili)
          : item.fiyatgaranti != null
            ? String(item.fiyatgaranti)
            : '',
      fiyatGarantiHarici:
        item.fiyatGarantiHarici != null
          ? String(item.fiyatGarantiHarici)
          : item.fiyat != null
            ? String(item.fiyat)
            : '',
      birimId: activeTab === 'parca' ? birimIdStr : '',
      foto: null,
      fotoSil: false,
      degisimParcaGarantisi: !!item.degisimParcaGarantisi,
      bagliUrunOzet,
    });
    setItemFormMode('edit');
    setFormError('');
    const category = categories.find((c) => (c.id || c._id) === item.kategoriId);
    setSelectedCategoryName(category ? category.kategoriAdi : '');
    setIsItemFormModalOpen(true);

    (async () => {
      if (!ids.length) return;
      const enriched = await Promise.all(
        ids.map(async (id) => {
          try {
            const r = await fetchProduct(id);
            const doc = r?.veri?.icerik || r?.veri?.urun || r?.veri;
            if (!doc) return { id, ad: id, kod: '—' };
            return { id, ad: getContentName(doc), kod: getContentCode(doc) };
          } catch {
            return { id, ad: id, kod: '—' };
          }
        }),
      );
      setItemFormData((prev) => ({ ...prev, bagliUrunOzet: enriched }));
    })();
  };

  // Ürünü pasife alma
  const handleDeactivateProduct = async (product) => {
    const productName = getContentName(product);
    const ok = await confirm({
      title: 'Ürünü pasifleştir',
      message: `"${productName}" ürününü pasif yapmak istediğinize emin misiniz?`,
      variant: 'warning',
      confirmLabel: 'Evet',
    });
    if (!ok) {
      return;
    }

    try {
      const productId = product.id || product._id;
      const response = await deactivateProduct(productId);
      
      if (!response?.basarili) {
        throw new Error(response?.mesaj || 'Ürün pasif yapılamadı');
      }
      
      if (window.showNotification) {
        window.showNotification('success', 'Ürün pasif yapıldı.');
      }
      reloadAccordionData();
    } catch (err) {
      console.error('[handleDeactivateProduct] Error:', err);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Ürün pasif yapılamadı'));
      }
    }
  };

  // Ürünü aktif yapma
  const handleActivateProduct = async (product) => {
    try {
      const productId = product.id || product._id;
      const response = await activateProduct(productId);
      
      if (!response?.basarili) {
        throw new Error(response?.mesaj || 'Ürün aktif yapılamadı');
      }
      
      if (window.showNotification) {
        window.showNotification('success', 'Ürün aktif yapıldı.');
      }
      reloadAccordionData();
    } catch (err) {
      console.error('[handleActivateProduct] Error:', err);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Ürün aktif yapılamadı'));
      }
    }
  };

  const tabLabels = {
    urun: 'Ürün',
    parca: 'Parça',
    islem: 'İşlem',
    ariza: 'Arıza',
  };

  const accordionSearchPlaceholder =
    activeTab === 'urun'
      ? 'Ürün Ara (Ürün Adı, Ürün Kodu, Mal Kodu, Model Kodu)'
      : activeTab === 'parca'
        ? isItemUrunMode
          ? 'Parça ara (parça ad/kod veya bağlı ürün ad/kod/mal/model)'
          : 'Parça Ara (Parça Adı, Parça Kodu, Açıklama)'
        : activeTab === 'islem'
          ? 'İşlem Ara (İşlem Adı, İşlem Kodu, Açıklama)'
          : activeTab === 'ariza'
            ? isItemUrunMode
              ? 'Arıza ara (arıza ad/kod veya bağlı ürün ad/kod/mal/model)'
              : 'Arıza Ara (Arıza Adı, Arıza Kodu, Açıklama)'
            : `${tabLabels[activeTab] || ''} ara (en az 3 karakter)...`;

  const handleMoveContentToCategory = async (contentId, targetCategoryId) => {
    const idStr = String(contentId || '');
    if (!idStr) return;
    const row = contentListForAccordion.find((r) => String(r.id || r._id) === idStr);
    if (!row) return;
    const currentKid =
      row.kategoriId == null || row.kategoriId === '' ? null : String(row.kategoriId);
    const targetKid =
      targetCategoryId == null || targetCategoryId === '' ? null : String(targetCategoryId);
    if (currentKid === targetKid) return;
    try {
      const res = await updateProduct(idStr, { kategoriId: targetKid });
      if (!res?.basarili) {
        throw new Error(res?.mesaj || res?.hata || 'Taşınamadı');
      }
      if (window.showNotification) {
        window.showNotification('success', 'Kategori güncellendi.');
      }
      reloadAccordionData();
    } catch (err) {
      console.error('[handleMoveContentToCategory]', err);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Kayıt taşınamadı'));
      }
    }
  };

  const handleBulkMoveParcaUncategorized = async (targetCategoryId, ids) => {
    const targetKid =
      targetCategoryId == null || targetCategoryId === '' ? null : String(targetCategoryId);
    if (!targetKid) {
      if (window.showNotification) {
        window.showNotification('error', 'Hedef kategori seçmelisiniz.');
      }
      return;
    }
    const list = Array.isArray(ids) ? ids.map(String).filter(Boolean) : [];
    if (list.length === 0) return;
    setBulkMoveInProgress(true);
    try {
      for (const idStr of list) {
        const res = await updateProduct(idStr, { kategoriId: targetKid });
        if (!res?.basarili) {
          throw new Error(res?.mesaj || res?.hata || 'Taşınamadı');
        }
      }
      if (window.showNotification) {
        window.showNotification(
          'success',
          `${list.length} ${activeTab === 'urun' ? 'ürün' : 'parça'} taşındı.`,
        );
      }
      clearContentBulkSelection();
      reloadAccordionData();
    } catch (err) {
      console.error('[handleBulkMoveParcaUncategorized]', err);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Toplu taşıma tamamlanamadı'));
      }
      reloadAccordionData();
    } finally {
      setBulkMoveInProgress(false);
    }
  };

  const handleBulkAssignParcaFiyat = async (ids, fiyat) => {
    const list = Array.isArray(ids) ? ids.map(String).filter(Boolean) : [];
    const fiyatNum = Number(fiyat);
    if (list.length === 0 || !Number.isFinite(fiyatNum) || fiyatNum < 0) return;
    setBulkFiyatInProgress(true);
    try {
      for (const idStr of list) {
        const res = await updateProduct(idStr, { fiyat: fiyatNum, icerikTipi: 'parca' });
        if (!res?.basarili) {
          throw new Error(res?.mesaj || res?.hata || 'Fiyat güncellenemedi');
        }
      }
      if (window.showNotification) {
        window.showNotification('success', `${list.length} parçanın fiyatı güncellendi.`);
      }
      setBulkFiyatModal(null);
      clearContentBulkSelection();
      reloadAccordionData();
    } catch (err) {
      console.error('[handleBulkAssignParcaFiyat]', err);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Toplu fiyat güncellemesi tamamlanamadı'));
      }
      reloadAccordionData();
    } finally {
      setBulkFiyatInProgress(false);
    }
  };

  const handleBulkAssignParcaFoto = async (ids, file) => {
    const list = Array.isArray(ids) ? ids.map(String).filter(Boolean) : [];
    if (list.length === 0 || !(file instanceof File)) return;
    setBulkFotoInProgress(true);
    try {
      for (const idStr of list) {
        const item = findContentItemById(idStr);
        if (item?.fotoUrl) {
          const delRes = await deleteParcaFoto(idStr);
          if (!delRes?.basarili) {
            throw new Error(delRes?.mesaj || delRes?.hata || 'Mevcut foto silinemedi');
          }
        }
        const res = await updateProduct(idStr, { foto: file, icerikTipi: 'parca' });
        if (!res?.basarili) {
          throw new Error(res?.mesaj || res?.hata || 'Foto eklenemedi');
        }
      }
      if (window.showNotification) {
        window.showNotification('success', `${list.length} parçaya foto eklendi.`);
      }
      closeBulkFotoModal();
      clearContentBulkSelection();
      reloadAccordionData();
    } catch (err) {
      console.error('[handleBulkAssignParcaFoto]', err);
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Toplu foto atama tamamlanamadı'));
      }
      reloadAccordionData();
    } finally {
      setBulkFotoInProgress(false);
    }
  };

  const applyCategoryMove = useCallback(
    async (sourceIdRaw, newParentIdRaw) => {
      const sid = String(sourceIdRaw || '');
      const tid = newParentIdRaw == null || newParentIdRaw === '' ? null : String(newParentIdRaw);
      if (!sid) return;
      if (tid !== null && sid === tid) return;

      const list = categories;
      if (tid != null && isCategoryAncestorOf(sid, tid, list)) {
        if (window.showNotification) {
          window.showNotification('error', 'Bir kategori kendi altındaki kategorinin altına taşınamaz.');
        }
        return;
      }

      const normParent = (x) => (x == null || x === '' ? null : String(x));
      const sourceRow = list.find((c) => String(c.id || c._id) === sid);
      if (!sourceRow) return;
      if (normParent(sourceRow.ustKategoriId) === normParent(tid)) {
        if (window.showNotification) {
          window.showNotification('info', 'Kategori zaten bu konumda.');
        }
        return;
      }

      try {
        const moveRes = await moveCategory(sourceRow.id || sourceRow._id, tid);
        if (!moveRes?.basarili) {
          throw new Error(moveRes?.mesaj || 'Kategori taşınamadı');
        }

        if (window.showNotification) {
          window.showNotification('success', 'Kategori taşındı.');
        }
        if (tid) {
          setExpandedCategoryIds((prev) => {
            const n = new Set(prev);
            n.add(String(tid));
            return n;
          });
        }
      } catch (err) {
        console.error('[applyCategoryMove]', err);
        if (window.showNotification) {
          window.showNotification('error', getErrorMessage(err, 'Kategori taşınamadı'));
        }
      } finally {
        fetchedCategoryChildrenRef.current = new Set();
        setConfirmedLeafCategoryIds(new Set());
        confirmedLeafRef.current = new Set();
        await loadCategories();
        reloadAccordionData();
      }
    },
    [categories, loadCategories, reloadAccordionData],
  );

  const renderCategoryContentsBody = (
    leafRows,
    isLeafContentsLoading,
    { showEmptyHint = true, categoryIdStr = null } = {},
  ) => {
    const contentBulkSelectActive =
      supportsContentBulkSelect &&
      !isItemUrunMode &&
      categoryIdStr &&
      contentBulkSelectCategoryId === categoryIdStr;
    const contentBulkAllSelected =
      contentBulkSelectActive &&
      leafRows.length > 0 &&
      leafRows.every((item) => contentBulkSelectedIds.has(String(item.id || item._id)));
    if (isLeafContentsLoading) {
      return (
        <div className="content-accordion-leaf-loading" role="status" aria-live="polite">
          <i className="fas fa-spinner fa-spin" aria-hidden />
          <span>Yükleniyor...</span>
        </div>
      );
    }
    return (
      activeTab === 'urun' || isItemUrunMode ? (
                isItemUrunMode ? (
                  leafRows.length === 0 ? (
                    showEmptyHint ? <p className="content-accordion-hint">Bu kategoride ürün yok.</p> : null
                  ) : (
                    <div className="table-container">
                      <table className="admins-table ctp-urun-select-table ctp-urun-select-table--name-only">
                        <thead>
                          <tr>
                            <th>Ürün Adı</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leafRows.map((product) => {
                            const pid = product.id || product._id;
                            const pidStr = String(pid);
                            const isSelected = selectedProductForParts && String(selectedProductForParts.id || selectedProductForParts._id) === pidStr;
                            const listSearchQ = isAccordionSearchActive ? debouncedListSearch.trim() : '';
                            return (
                              <tr
                                key={pid}
                                className={isSelected ? 'ctp-urun-row--selected' : undefined}
                                onClick={() => {
                                  const alreadySelected = selectedProductForParts && String(selectedProductForParts.id || selectedProductForParts._id) === pidStr;
                                  if (alreadySelected) {
                                    setSelectedProductForParts(null);
                                  } else {
                                    setSelectedProductForParts(product);
                                    loadLinkedPartsForProduct(pidStr);
                                  }
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                <td className="name-cell" style={{ fontWeight: 500 }}>
                                  <HighlightMatch text={getContentName(product)} query={listSearchQ} />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
              ) : (
                leafRows.length === 0 ? (
                  showEmptyHint ? <p className="content-accordion-hint">Bu kategoride kayıt yok. + ile ekleyin veya Taşı ile başka kategoriye taşıyın.</p> : null
                ) : (
                  <div className="table-container">
                    <table className={`admins-table${contentBulkSelectActive ? ' admins-table--bulk-select' : ''}`}>
                      <thead>
                        <tr>
                          {contentBulkSelectActive && (
                            <th className="ctp-bulk-check-col" scope="col">
                              <input
                                type="checkbox"
                                className="ctp-bulk-check-input"
                                checked={contentBulkAllSelected}
                                onChange={(e) =>
                                  toggleContentBulkSelectAllInCategory(leafRows, e.target.checked)
                                }
                                aria-label="Tümünü seç"
                              />
                            </th>
                          )}
                          <th>Foto</th>
                          <th>Ürün Kodu</th>
                          <th>Ürün Adı</th>
                          <th>Mal kodu</th>
                          <th>Model kodu</th>
                          <th title="Müşteriye kurulum tamamlandığında cihaz kaydına yazılan garanti süresi">Ürün garantisi (ay)</th>
                          <th></th>
                          {!contentBulkSelectActive && (
                            <th
                              className="ctp-table-tasi-col"
                              scope="col"
                              title="Kategoriye taşı"
                              aria-label="Taşı"
                            >
                              <i className="fas fa-arrows-alt ctp-table-tasi-th-icon" aria-hidden />
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {leafRows.map((product) => {
                          const pid = product.id || product._id;
                          const pidStr = String(pid);
                          const isBulkChecked = contentBulkSelectedIds.has(pidStr);
                          return (
                            <tr
                              key={pid}
                              className={isBulkChecked ? 'ctp-bulk-row--selected' : undefined}
                            >
                              {contentBulkSelectActive && (
                                <td className="ctp-bulk-check-col">
                                  <input
                                    type="checkbox"
                                    className="ctp-bulk-check-input"
                                    checked={isBulkChecked}
                                    onChange={(e) => toggleContentBulkItem(pidStr, e.target.checked)}
                                    aria-label={`${getContentName(product)} seç`}
                                  />
                                </td>
                              )}
                              <td>
                                {product.fotoUrl ? (
                                  <button
                                    type="button"
                                    className="photo-thumb-btn"
                                    onClick={() => setSelectedPhotoUrl(getParcaFotoUrl(product.fotoUrl))}
                                    style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                                    title="Büyüt"
                                  >
                                    <img
                                      src={getParcaFotoUrl(product.fotoUrl)}
                                      alt=""
                                      style={{
                                        width: 48,
                                        height: 48,
                                        objectFit: 'cover',
                                        border: '1px solid var(--border-gray)',
                                      }}
                                    />
                                  </button>
                                ) : (
                                  <div
                                    style={{
                                      width: 48,
                                      height: 48,
                                      border: '1px solid var(--border-gray)',
                                      background: 'var(--bg-light, #f5f5f5)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'var(--text-gray)',
                                    }}
                                    title="Fotoğraf yok"
                                  >
                                    <i className="fas fa-image" style={{ fontSize: 20, opacity: 0.5 }}></i>
                                  </div>
                                )}
                              </td>
                              <td className="name-cell" style={{ color: 'var(--primary-blue)', fontWeight: 500 }}>
                                <HighlightMatch text={getContentCode(product)} query={isAccordionSearchActive ? debouncedListSearch.trim() : ''} />
                              </td>
                              <td style={{ fontWeight: 500 }}>
                                <HighlightMatch text={getContentName(product)} query={isAccordionSearchActive ? debouncedListSearch.trim() : ''} />
                              </td>
                              <td>
                                <HighlightMatch text={getContentMalKodu(product)} query={isAccordionSearchActive ? debouncedListSearch.trim() : ''} />
                              </td>
                              <td>
                                <HighlightMatch text={getContentModelKodu(product)} query={isAccordionSearchActive ? debouncedListSearch.trim() : ''} />
                              </td>
                              <td>
                                {product.kurulumGarantiSuresiAy != null &&
                                Number.isFinite(Number(product.kurulumGarantiSuresiAy))
                                  ? product.kurulumGarantiSuresiAy
                                  : '—'}
                              </td>
                              <td className="table-actions">
                                {!contentBulkSelectActive &&
                                  (activeSubTab !== 'pasif-urunler' ? (
                                    <>
                                      <button
                                        type="button"
                                        className="category-action-btn edit-btn"
                                        onClick={() => handleEditProduct(product)}
                                        title="Düzenle"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="category-action-btn delete-btn"
                                        onClick={() => handleDeactivateProduct(product)}
                                        title="Pasif Yap"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      className="category-action-btn move-btn"
                                      onClick={() => handleActivateProduct(product)}
                                      title="Aktif Yap"
                                    >
                                      <i className="fas fa-check"></i>
                                    </button>
                                  ))}
                              </td>
                              {!contentBulkSelectActive && (
                                <td className="ctp-table-tasi-col">
                                  <button
                                    type="button"
                                    className="ctp-tasi-icon-btn"
                                    title="Kategoriye taşı"
                                    aria-label="Taşı"
                                    onClick={() => setMoveTargetModal({ kind: 'icerik', item: product })}
                                  >
                                    <i className="fas fa-arrows-alt" aria-hidden />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )
              ) : (
                leafRows.length === 0 ? (
                  showEmptyHint ? <p className="content-accordion-hint">Bu kategoride kayıt yok. + ile ekleyin veya Taşı ile başka kategoriye taşıyın.</p> : null
                ) : (
                  <div className="table-container">
                    <table className={`admins-table${contentBulkSelectActive ? ' admins-table--bulk-select' : ''}`}>
                      <thead>
                        <tr>
                          {contentBulkSelectActive && (
                            <th className="ctp-bulk-check-col" scope="col">
                              <input
                                type="checkbox"
                                className="ctp-bulk-check-input"
                                checked={contentBulkAllSelected}
                                aria-label="Bu kategorideki tüm kayıtları seç"
                                onChange={(e) =>
                                  toggleContentBulkSelectAllInCategory(leafRows, e.target.checked)
                                }
                              />
                            </th>
                          )}
                          {activeTab === 'parca' && <th>Foto</th>}
                          <th>{getFieldNames(activeTab).labelKod}</th>
                          <th>{getFieldNames(activeTab).labelAdi}</th>
                          {activeTab === 'parca' && (
                            <th title="Randevuda değişimde 6 ay parça garantisi">Değ. garanti</th>
                          )}
                          {activeTab === 'parca' && <th>Birim</th>}
                          {(activeTab === 'parca' || activeTab === 'islem') && (
                            <>
                              {activeTab === 'parca' ? (
                                <>
                                  <th>Net fiyat (₺)</th>
                                  <th>Brüt fiyat (₺)</th>
                                </>
                              ) : (
                                <>
                                  <th>Garanti dahili (₺)</th>
                                  <th>Garanti harici (₺)</th>
                                </>
                              )}
                            </>
                          )}
                          <th></th>
                          {!contentBulkSelectActive && (
                            <th
                              className="ctp-table-tasi-col"
                              scope="col"
                              title="Kategoriye taşı"
                              aria-label="Taşı"
                            >
                              <i className="fas fa-arrows-alt ctp-table-tasi-th-icon" aria-hidden />
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {leafRows.map((item) => {
                          const iid = item.id || item._id;
                          const iidStr = String(iid);
                          const isBulkChecked = contentBulkSelectedIds.has(iidStr);
                          return (
                            <tr
                              key={iid}
                              className={isBulkChecked ? 'ctp-bulk-row--selected' : undefined}
                            >
                              {contentBulkSelectActive && (
                                <td className="ctp-bulk-check-col">
                                  <input
                                    type="checkbox"
                                    className="ctp-bulk-check-input"
                                    checked={isBulkChecked}
                                    aria-label={`${getContentName(item)} seç`}
                                    onChange={(e) => toggleContentBulkItem(iidStr, e.target.checked)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </td>
                              )}
                              {activeTab === 'parca' && (
                                <td>
                                  {item.fotoUrl ? (
                                    <button
                                      type="button"
                                      className="photo-thumb-btn"
                                      onClick={() => setSelectedPhotoUrl(getParcaFotoUrl(item.fotoUrl))}
                                      style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                                      title="Büyüt"
                                    >
                                      <img
                                        src={getParcaFotoUrl(item.fotoUrl)}
                                        alt=""
                                        style={{
                                          width: 48,
                                          height: 48,
                                          objectFit: 'cover',
                                          border: '1px solid var(--border-gray)',
                                        }}
                                      />
                                    </button>
                                  ) : (
                                    <div
                                      style={{
                                        width: 48,
                                        height: 48,
                                        border: '1px solid var(--border-gray)',
                                        background: 'var(--bg-light, #f5f5f5)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--text-gray)',
                                      }}
                                      title="Fotoğraf yok"
                                    >
                                      <i className="fas fa-image" style={{ fontSize: 20, opacity: 0.5 }}></i>
                                    </div>
                                  )}
                                </td>
                              )}
                              <td className="name-cell" style={{ color: 'var(--primary-blue)', fontWeight: 500 }}>
                                <HighlightMatch text={getContentCode(item)} query={isAccordionSearchActive ? debouncedListSearch.trim() : ''} />
                              </td>
                              <td style={{ fontWeight: 500 }}>
                                <HighlightMatch text={getContentName(item)} query={isAccordionSearchActive ? debouncedListSearch.trim() : ''} />
                              </td>
                              {activeTab === 'parca' && (
                                <td style={{ textAlign: 'center' }} title="Randevuda değişimde 6 ay parça garantisi">
                                  {item.degisimParcaGarantisi === true ? (
                                    <i className="fas fa-check-circle" style={{ color: '#16a34a' }} aria-label="Evet"></i>
                                  ) : (
                                    <span style={{ color: 'var(--text-gray)' }}>—</span>
                                  )}
                                </td>
                              )}
                              {activeTab === 'parca' && <td>{getParcaBirimLabel(item)}</td>}
                              {(activeTab === 'parca' || activeTab === 'islem') && (
                                <>
                                  {activeTab === 'parca' ? (
                                    <>
                                      <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyatgaranti)}</td>
                                      <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyat)}</td>
                                    </>
                                  ) : (
                                    <>
                                      <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyatGarantiDahili ?? item.fiyatgaranti)}</td>
                                      <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyatGarantiHarici ?? item.fiyat)}</td>
                                    </>
                                  )}
                                </>
                              )}
                              <td className="table-actions">
                                {!contentBulkSelectActive &&
                                  (activeItemSubTab === 'aktif' ? (
                                    <>
                                      <button
                                        type="button"
                                        className="category-action-btn edit-btn"
                                        onClick={() => openEditItemModal(item)}
                                        title="Düzenle"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="category-action-btn delete-btn"
                                        onClick={async () => {
                                          const itemName = getContentName(item);
                                          const okPasif = await confirm({
                                            title: 'Pasifleştir',
                                            message: `"${itemName}" ${tabLabels[activeTab].toLowerCase()}nı pasif yapmak istediğinize emin misiniz?`,
                                            variant: 'warning',
                                            confirmLabel: 'Evet',
                                          });
                                          if (!okPasif) {
                                            return;
                                          }
                                          try {
                                            await deactivateProduct(iid);
                                            if (window.showNotification) {
                                              window.showNotification(
                                                'success',
                                                `${tabLabels[activeTab]} başarıyla pasifleştirildi`,
                                              );
                                            }
                                            reloadAccordionData();
                                          } catch (err) {
                                            console.error('[handleDeactivateItem] Error:', err);
                                            if (window.showNotification) {
                                              window.showNotification(
                                                'error',
                                                getErrorMessage(err, `${tabLabels[activeTab]} pasif yapılamadı`),
                                              );
                                            }
                                          }
                                        }}
                                        title="Pasif Yap"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      className="category-action-btn move-btn"
                                      onClick={async () => {
                                        try {
                                          await activateProduct(iid);
                                          if (window.showNotification) {
                                            window.showNotification(
                                              'success',
                                              `${tabLabels[activeTab]} başarıyla aktifleştirildi`,
                                            );
                                          }
                                          reloadAccordionData();
                                        } catch (err) {
                                          console.error('[handleActivateItem] Error:', err);
                                          if (window.showNotification) {
                                            window.showNotification(
                                              'error',
                                              getErrorMessage(err, `${tabLabels[activeTab]} aktif yapılamadı`),
                                            );
                                          }
                                        }
                                      }}
                                      title="Aktif Yap"
                                    >
                                      <i className="fas fa-check"></i>
                                    </button>
                                  ))}
                              </td>
                              {!contentBulkSelectActive && (
                                <td className="ctp-table-tasi-col">
                                  <button
                                    type="button"
                                    className="ctp-tasi-icon-btn"
                                    title="Kategoriye taşı"
                                    aria-label="Taşı"
                                    onClick={() => setMoveTargetModal({ kind: 'icerik', item })}
                                  >
                                    <i className="fas fa-arrows-alt" aria-hidden />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )
    );
  };

  const renderContentAccordionTree = (categoryTree, level = 0) => {
    if (!categoryTree || categoryTree.length === 0) {
      return null;
    }

    const paddingLeftPxForLevel = level * 20 + 12;

    const rowNodes = categoryTree.map((category) => {
      const categoryId = category.id || category._id;
      const categoryIdStr = String(categoryId);
      const hasChildrenInTree = category.children && category.children.length > 0;
      const isConfirmedLeaf = confirmedLeafCategoryIds.has(categoryIdStr);
      const treatAsBranch = isAccordionSearchActive ? hasChildrenInTree : hasChildrenInTree || !isConfirmedLeaf;
      const isExpanded = Array.from(expandedCategoryIds).some((id) => String(id) === categoryIdStr);
      const leafRows = itemsByCategoryId.get(categoryIdStr) || [];
      const isLeafContentsLoading = leafContentsLoadingIds.has(categoryIdStr);

      if (!treatAsBranch) {
        const leafRowInner = (
          <>
            <button
              type="button"
              className="category-toggle-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleCategory(categoryId);
              }}
              aria-expanded={isExpanded}
            >
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
            </button>
            <span className="content-accordion-title">
              {category.kategoriAdi}
            </span>
            {isItemUrunMode ? (
              <span className="ctp-cat-actions">
                <button
                  type="button"
                  className="ctp-cat-action-btn ctp-cat-action-btn--edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMoveTargetModal({ kind: 'kategori', category });
                  }}
                  title="Kategoriyi taşı"
                >
                  <i className="fas fa-arrows-alt" />
                </button>
              </span>
            ) : (
              <span className="ctp-cat-actions">
                {renderContentBulkSelectControls(categoryIdStr)}
                <button
                  type="button"
                  className="ctp-cat-action-btn ctp-cat-action-btn--edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMoveTargetModal({ kind: 'kategori', category });
                  }}
                  title="Kategoriyi taşı"
                >
                  <i className="fas fa-arrows-alt" />
                </button>
                <button type="button" className="ctp-cat-action-btn ctp-cat-action-btn--add" onClick={(e) => { e.stopPropagation(); openAddContentForLeafCategory(category); }} title={`${tabLabels[activeTab]} Ekle`}>
                  <i className="fas fa-plus-circle" />
                </button>
                {excelEditExportEnabled ? (
                  <>
                    <button
                      type="button"
                      className="ctp-cat-action-btn ctp-cat-action-btn--excel-export"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExcelExportCategory(category);
                      }}
                      title="Excel ile dışarı aktar"
                    >
                      <i className="fas fa-file-download" />
                    </button>
                    <button
                      type="button"
                      className="ctp-cat-action-btn ctp-cat-action-btn--excel-edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExcelEditCategory(category);
                      }}
                      title="Excel ile düzenle"
                    >
                      <i className="fas fa-file-import" />
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className="ctp-cat-action-btn ctp-cat-action-btn--excel"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExcelImportCategory({
                      id: String(category.id || category._id),
                      name: category.kategoriAdi || '',
                    });
                  }}
                  title="Excel'den aktar"
                >
                  <i className="fas fa-file-excel" />
                </button>
                <button type="button" className="ctp-cat-action-btn ctp-cat-action-btn--add" onClick={(e) => { e.stopPropagation(); openCategoryFormForCreate(category); }} title="Alt Kategori Ekle">
                  <i className="fas fa-folder-plus" />
                </button>
                <button type="button" className="ctp-cat-action-btn ctp-cat-action-btn--edit" onClick={(e) => { e.stopPropagation(); openCategoryFormForEdit(category); }} title="Kategori Düzenle">
                  <i className="fas fa-pen" />
                </button>
                <button type="button" className="ctp-cat-action-btn ctp-cat-action-btn--deactivate" onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category); }} title="Pasif Yap">
                  <i className="fas fa-ban" />
                </button>
              </span>
            )}
          </>
        );
        const leafBulkSelectActive =
          supportsContentBulkSelect && contentBulkSelectCategoryId === categoryIdStr;
        return (
          <div key={categoryId} className="content-accordion-item">
            <div className="content-accordion-leaf">
              <div
                className={`content-accordion-row${leafBulkSelectActive ? ' content-accordion-row--bulk-select-active' : ''}`}
                style={{ paddingLeft: `${paddingLeftPxForLevel}px` }}
                onClick={() => handleAccordionBranchToggle(categoryId, isExpanded)}
              >
                {leafRowInner}
              </div>
              {isExpanded &&
                renderCategoryContentsBody(leafRows, isLeafContentsLoading, {
                  showEmptyHint: true,
                  categoryIdStr,
                })}
            </div>
          </div>
        );
      }

      const branchRowInner = (
        <>
          <button
            type="button"
            className="category-toggle-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAccordionBranchToggle(categoryId, isExpanded);
            }}
          >
            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
          </button>
          <span className="content-accordion-title">
            {category.kategoriAdi}
          </span>
          {isItemUrunMode ? (
            <span className="ctp-cat-actions">
              <button
                type="button"
                className="ctp-cat-action-btn ctp-cat-action-btn--edit"
                onClick={(e) => {
                  e.stopPropagation();
                  setMoveTargetModal({ kind: 'kategori', category });
                }}
                title="Kategoriyi taşı"
              >
                <i className="fas fa-arrows-alt" />
              </button>
            </span>
          ) : (
            <span className="ctp-cat-actions">
              {renderContentBulkSelectControls(categoryIdStr)}
              <button
                type="button"
                className="ctp-cat-action-btn ctp-cat-action-btn--edit"
                onClick={(e) => {
                  e.stopPropagation();
                  setMoveTargetModal({ kind: 'kategori', category });
                }}
                title="Kategoriyi taşı"
              >
                <i className="fas fa-arrows-alt" />
              </button>
              <button type="button" className="ctp-cat-action-btn ctp-cat-action-btn--add" onClick={(e) => { e.stopPropagation(); openAddContentForLeafCategory(category); }} title={`${tabLabels[activeTab]} Ekle`}>
                <i className="fas fa-plus-circle" />
              </button>
              <button type="button" className="ctp-cat-action-btn ctp-cat-action-btn--add" onClick={(e) => { e.stopPropagation(); openCategoryFormForCreate(category); }} title="Alt Kategori Ekle">
                <i className="fas fa-folder-plus" />
              </button>
              <button type="button" className="ctp-cat-action-btn ctp-cat-action-btn--edit" onClick={(e) => { e.stopPropagation(); openCategoryFormForEdit(category); }} title="Kategori Düzenle">
                <i className="fas fa-pen" />
              </button>
              <button type="button" className="ctp-cat-action-btn ctp-cat-action-btn--deactivate" onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category); }} title="Pasif Yap">
                <i className="fas fa-ban" />
              </button>
            </span>
          )}
        </>
      );

      const branchBulkSelectActive =
        supportsContentBulkSelect && contentBulkSelectCategoryId === categoryIdStr;

      return (
        <div key={categoryId} className="content-accordion-item">
          <div
            className={`content-accordion-row${branchBulkSelectActive ? ' content-accordion-row--bulk-select-active' : ''}`}
            style={{ paddingLeft: `${paddingLeftPxForLevel}px` }}
            onClick={() => handleAccordionBranchToggle(categoryId, isExpanded)}
          >
            {branchRowInner}
          </div>
          {isExpanded && (
            <div className="content-accordion-children">
              {hasChildrenInTree ? renderContentAccordionTree(category.children, level + 1) : null}
              <div className="content-accordion-direct-contents">
                {renderCategoryContentsBody(leafRows, isLeafContentsLoading, {
                  showEmptyHint: !hasChildrenInTree,
                  categoryIdStr,
                })}
              </div>
            </div>
          )}
        </div>
      );
    });

    return <>{rowNodes}</>;
  };

  // Artık frontend'de filtreleme yapmıyoruz, backend'den gelen veriler zaten filtrelenmiş
  // filteredProducts kaldırıldı, direkt products kullanılıyor

  if (!VALID_CONTENT_TYPES.includes(contentType)) {
    return <Navigate to="/contents/urun" replace />;
  }

  return (
    <main
      className={`main-content content-management${isItemUrunMode ? ' content-management--item-urun-split' : ''}`}
      key={contentType}
    >
      <div className="content-header">
        <h1>{PAGE_TITLES[activeTab] || 'İçerik Yönetimi'}</h1>
      </div>

      {/* Kontrol çubuğu */}
      <div className="ctp-toolbar">
        <div className="ctp-toolbar__left">
          <button type="button" className="primary-btn" onClick={() => openCategoryFormForCreate(null)}>
            <i className="fas fa-plus"></i>
            <span>Kategori Ekle</span>
          </button>
        </div>
        <div className="ctp-toolbar__right">
          <label className="ctp-toolbar-sort">
            <span className="ctp-toggle-text">Sıralama</span>
            <select
              className="ctp-toolbar-sort__select"
              value={listSort}
              onChange={(e) => setListSortInUrl(e.target.value)}
              aria-label="Liste sıralaması"
            >
              {LIST_SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <div className="ctp-toggle-label">
            <span className="ctp-toggle-text">Aktif/Pasif</span>
            <button
              type="button"
              className={`ctp-toggle-switch${isAktifFromUrl ? ' ctp-toggle-switch--active' : ''}`}
              onClick={() => setAktifToggle(!isAktifFromUrl)}
              role="switch"
              aria-checked={isAktifFromUrl}
            >
              <span className="ctp-toggle-switch__knob" />
            </button>
          </div>
          {(activeTab === 'parca' || activeTab === 'ariza') && (
            <div className="ctp-segmented">
              <button
                type="button"
                className={`ctp-segmented__btn${gorunumFromUrl === 'kategori' ? ' ctp-segmented__btn--active' : ''}`}
                onClick={() => setGorunumToggle('kategori')}
              >
                Kategori bazlı
              </button>
              <button
                type="button"
                className={`ctp-segmented__btn${gorunumFromUrl === 'urun' ? ' ctp-segmented__btn--active' : ''}`}
                onClick={() => setGorunumToggle('urun')}
              >
                Ürün bazlı
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="categories-section">
        <div className="categories-header content-management-categories-header">
          <div className="search-box content-management-search-box-full">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder={accordionSearchPlaceholder}
                  value={searchInput}
                  onChange={handleSearchChange}
                  maxLength={100}
                  autoComplete="off"
                />
                {showAccordionSearchBoxSpinner && (
                  <i
                    className="fas fa-spinner fa-spin"
                    style={{ right: searchInput.length > 0 ? '40px' : '10px' }}
                    aria-hidden
                  />
                )}
                {searchInput.length > 0 && (
                  <button
                    type="button"
                    className="search-clear-btn"
                    onClick={handleClearSearch}
                    title="Aramayı temizle"
                    aria-label="Aramayı temizle"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              {searchInput.trim().length > 0 && searchInput.trim().length < 3 && (
                <p className="content-management-search-hint">Aramayı başlatmak için en az 3 karakter girin.</p>
              )}
            </div>

            <div className="categories-content">
              {showItemUrunSearchFlatList ? (
                <>
                  {loading ? (
                    <div className="loading-state">
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Yükleniyor...</span>
                    </div>
                  ) : error ? (
                    <div className="error-state">
                      <i className="fas fa-exclamation-circle"></i>
                      <span>{error}</span>
                    </div>
                  ) : sortedItemUrunFlatSearchItems.length === 0 ? (
                    <p className="content-accordion-hint" style={{ margin: '16px 0' }}>
                      Arama teriminizle eşleşen {tabLabels[activeTab]?.toLowerCase()} bulunamadı.
                    </p>
                  ) : (
                    <div className="table-container">
                      <table className="admins-table">
                        <thead>
                          <tr>
                            {activeTab === 'parca' && <th>Foto</th>}
                            <th>{getFieldNames(activeTab).labelKod}</th>
                            <th>{getFieldNames(activeTab).labelAdi}</th>
                            {activeTab === 'parca' && <th>Birim</th>}
                            {(activeTab === 'parca' || activeTab === 'islem') && <th>Brüt fiyat</th>}
                            <th></th>
                            <th
                              className="ctp-table-tasi-col"
                              scope="col"
                              title="Kategoriye taşı"
                              aria-label="Taşı"
                            >
                              <i className="fas fa-arrows-alt ctp-table-tasi-th-icon" aria-hidden />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedItemUrunFlatSearchItems.map((item) => {
                            const iid = item.id || item._id;
                            return (
                              <tr key={iid}>
                                {activeTab === 'parca' && (
                                  <td>{item.fotoUrl ? <button type="button" className="photo-thumb-btn" onClick={() => setSelectedPhotoUrl(getParcaFotoUrl(item.fotoUrl))} style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}><img src={getParcaFotoUrl(item.fotoUrl)} alt="" style={{ width: 40, height: 40, objectFit: 'cover' }} /></button> : <span style={{ color: 'var(--text-gray)' }}>—</span>}</td>
                                )}
                                <td><HighlightMatch text={getContentCode(item)} query={isAccordionSearchActive ? debouncedListSearch.trim() : ''} /></td>
                                <td><HighlightMatch text={getContentName(item)} query={isAccordionSearchActive ? debouncedListSearch.trim() : ''} /></td>
                                {activeTab === 'parca' && <td>{getParcaBirimLabel(item)}</td>}
                                {(activeTab === 'parca' || activeTab === 'islem') && <td>{item.fiyat != null ? `₺${item.fiyat}` : '—'}</td>}
                                <td className="table-actions">
                                  <button type="button" className="category-action-btn edit-btn" onClick={() => openEditItemModal(item)} title="Düzenle"><i className="fas fa-edit" /></button>
                                  {isAktifFromUrl ? (
                                    <button type="button" className="category-action-btn delete-btn" onClick={() => handleDeactivateProduct(item)} title="Pasif yap"><i className="fas fa-ban" /></button>
                                  ) : (
                                    <button type="button" className="category-action-btn add-btn" onClick={() => handleActivateProduct(item)} title="Aktif yap"><i className="fas fa-check" /></button>
                                  )}
                                </td>
                                <td className="ctp-table-tasi-col">
                                  <button
                                    type="button"
                                    className="ctp-tasi-icon-btn"
                                    title="Kategoriye taşı"
                                    aria-label="Taşı"
                                    onClick={() => setMoveTargetModal({ kind: 'icerik', item })}
                                  >
                                    <i className="fas fa-arrows-alt" aria-hidden />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : loading ? (
                <div className="loading-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Yükleniyor...</span>
                </div>
              ) : error ? (
                <div className="error-state">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{error}</span>
                </div>
              ) : categories.length === 0 && (itemsByCategoryId.get('__none__') || []).length === 0 ? (
                <div className="empty-state content-accordion-empty">
                  <i className="fas fa-folder-open"></i>
                  <span>Henüz bu içerik tipinde kategori bulunmamaktadır.</span>
                </div>
              ) : (
                <>
                  {(() => {
                    const accordionBlock = (
                      <>
                  <div className="content-accordion">
                    {isUrunTreeSearchActive ? (
                      sortedUrunTreeSearchResults.length === 0 ? (
                        <p className="content-accordion-hint" style={{ margin: '16px 0' }}>
                          Arama teriminizle eşleşen ürün bulunamadı.
                        </p>
                      ) : (
                        <div className="table-container">
                          <table className="admins-table ctp-urun-select-table ctp-urun-select-table--name-only">
                            <thead>
                              <tr>
                                <th>Ürün Adı</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedUrunTreeSearchResults.map((product) => {
                                const pid = product.id || product._id;
                                const pidStr = String(pid);
                                const isSelected = selectedProductForParts && String(selectedProductForParts.id || selectedProductForParts._id) === pidStr;
                                return (
                                  <tr
                                    key={pid}
                                    className={isSelected ? 'ctp-urun-row--selected' : undefined}
                                    onClick={() => {
                                      if (isSelected) {
                                        setSelectedProductForParts(null);
                                      } else {
                                        setSelectedProductForParts(product);
                                        loadLinkedPartsForProduct(pidStr);
                                      }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <td className="name-cell" style={{ fontWeight: 500 }}>
                                      <HighlightMatch text={getContentName(product)} query={debouncedUrunTreeSearch} />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )
                    ) : isAccordionSearchActive &&
                    contentAccordionCategoryTree.length === 0 &&
                    (itemsByCategoryId.get('__none__') || []).length === 0 ? (
                      <p className="content-accordion-hint" style={{ margin: '16px 0' }}>
                        Arama teriminizle eşleşen içerik bulunamadı.
                      </p>
                    ) : (
                      <>
                        {renderContentAccordionTree(contentAccordionCategoryTree, 0)}
                        {(itemsByCategoryId.get('__none__') || []).length > 0 && (
                          <div className="content-accordion-item content-accordion-item--uncategorized">
                            <div className="content-accordion-leaf">
                              <div
                                className="content-accordion-row"
                                onClick={() => setUncategorizedExpanded((v) => !v)}
                              >
                                <button
                                  type="button"
                                  className="category-toggle-btn"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setUncategorizedExpanded((v) => !v);
                                  }}
                                  aria-expanded={uncategorizedExpanded}
                                >
                                  <i className={`fas fa-chevron-${uncategorizedExpanded ? 'down' : 'right'}`} />
                                </button>
                                <span className="content-accordion-title" style={{ color: '#dc2626', fontWeight: 700 }}>
                                  KATEGORİSİZ
                                </span>
                                <span style={{ fontSize: 12, color: '#dc2626', opacity: 0.7, marginLeft: 8 }}>
                                  ({(itemsByCategoryId.get('__none__') || []).length})
                                </span>
                                {activeTab === 'parca' &&
                                  (itemsByCategoryId.get('__none__') || []).length > 0 && (
                                    <button
                                      type="button"
                                      className="secondary-btn"
                                      style={{
                                        marginLeft: 'auto',
                                        flexShrink: 0,
                                        fontSize: 12,
                                        padding: '4px 10px',
                                      }}
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const uncat = itemsByCategoryId.get('__none__') || [];
                                        const ids = uncat
                                          .map((it) => String(it.id || it._id))
                                          .filter(Boolean);
                                        if (ids.length === 0) return;
                                        const bulkOk = await confirm({
                                          title: 'Parçaları taşı',
                                          message: `Şu an kategorisiz ${ids.length} parça seçtiğiniz kategoriye taşınacak. Devam?`,
                                          variant: 'info',
                                          confirmLabel: 'Evet',
                                        });
                                        if (!bulkOk) return;
                                        setMoveTargetModal({ kind: 'icerik-bulk', ids });
                                      }}
                                    >
                                      Tümünü taşı
                                    </button>
                                  )}
                              </div>
                              {uncategorizedExpanded && (
                                <div className="table-container">
                                  <table className="admins-table">
                                    <thead>
                                      <tr>
                                        {(activeTab === 'urun' || activeTab === 'parca') && <th>Foto</th>}
                                        <th>{getFieldNames(activeTab).labelKod}</th>
                                        <th>{getFieldNames(activeTab).labelAdi}</th>
                                        {activeTab === 'urun' && <th>Mal kodu</th>}
                                        {activeTab === 'urun' && <th>Model kodu</th>}
                                        {activeTab === 'urun' && (
                                          <th title="Müşteriye kurulum tamamlandığında cihaz kaydına yazılan garanti süresi">Ürün garantisi (ay)</th>
                                        )}
                                        {activeTab === 'parca' && (
                                          <th title="Randevuda değişimde 6 ay parça garantisi">Değ. garanti</th>
                                        )}
                                        {activeTab === 'parca' && <th>Birim</th>}
                                        {(activeTab === 'parca' || activeTab === 'islem') && (
                                          <>
                                            {activeTab === 'parca' ? (
                                              <>
                                                <th>Net fiyat (₺)</th>
                                                <th>Brüt fiyat (₺)</th>
                                              </>
                                            ) : (
                                              <>
                                                <th>Garanti dahili (₺)</th>
                                                <th>Garanti harici (₺)</th>
                                              </>
                                            )}
                                          </>
                                        )}
                                        <th></th>
                                        <th
                                          className="ctp-table-tasi-col"
                                          scope="col"
                                          title="Kategoriye taşı"
                                          aria-label="Taşı"
                                        >
                                          <i className="fas fa-arrows-alt ctp-table-tasi-th-icon" aria-hidden />
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(itemsByCategoryId.get('__none__') || []).map((item) => {
                                        const iid = item.id || item._id;
                                        const iidStr = String(iid);
                                        return (
                                          <tr key={iid}>
                                            {(activeTab === 'urun' || activeTab === 'parca') && (
                                              <td>
                                                {item.fotoUrl ? (
                                                  <button
                                                    type="button"
                                                    className="photo-thumb-btn"
                                                    onClick={() => setSelectedPhotoUrl(getParcaFotoUrl(item.fotoUrl))}
                                                    style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                                                    title="Büyüt"
                                                  >
                                                    <img
                                                      src={getParcaFotoUrl(item.fotoUrl)}
                                                      alt=""
                                                      style={{
                                                        width: 48,
                                                        height: 48,
                                                        objectFit: 'cover',
                                                        border: '1px solid var(--border-gray)',
                                                      }}
                                                    />
                                                  </button>
                                                ) : (
                                                  <div
                                                    style={{
                                                      width: 48,
                                                      height: 48,
                                                      border: '1px solid var(--border-gray)',
                                                      background: 'var(--bg-light, #f5f5f5)',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      color: 'var(--text-gray)',
                                                    }}
                                                    title="Fotoğraf yok"
                                                  >
                                                    <i className="fas fa-image" style={{ fontSize: 20, opacity: 0.5 }}></i>
                                                  </div>
                                                )}
                                              </td>
                                            )}
                                            <td className="name-cell" style={{ color: 'var(--primary-blue)', fontWeight: 500 }}>
                                              {getContentCode(item)}
                                            </td>
                                            <td style={{ fontWeight: 500 }}>{getContentName(item)}</td>
                                            {activeTab === 'urun' && <td>{getContentMalKodu(item)}</td>}
                                            {activeTab === 'urun' && <td>{getContentModelKodu(item)}</td>}
                                            {activeTab === 'urun' && (
                                              <td>
                                                {item.kurulumGarantiSuresiAy != null &&
                                                Number.isFinite(Number(item.kurulumGarantiSuresiAy))
                                                  ? item.kurulumGarantiSuresiAy
                                                  : '—'}
                                              </td>
                                            )}
                                            {activeTab === 'parca' && (
                                              <td style={{ textAlign: 'center' }} title="Randevuda değişimde 6 ay parça garantisi">
                                                {item.degisimParcaGarantisi === true ? (
                                                  <i className="fas fa-check-circle" style={{ color: '#16a34a' }} aria-label="Evet"></i>
                                                ) : (
                                                  <span style={{ color: 'var(--text-gray)' }}>—</span>
                                                )}
                                              </td>
                                            )}
                                            {activeTab === 'parca' && <td>{getParcaBirimLabel(item)}</td>}
                                            {(activeTab === 'parca' || activeTab === 'islem') && (
                                              <>
                                                {activeTab === 'parca' ? (
                                                  <>
                                                    <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyatgaranti)}</td>
                                                    <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyat)}</td>
                                                  </>
                                                ) : (
                                                  <>
                                                    <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyatGarantiDahili ?? item.fiyatgaranti)}</td>
                                                    <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyatGarantiHarici ?? item.fiyat)}</td>
                                                  </>
                                                )}
                                              </>
                                            )}
                                            <td className="table-actions">
                                              {activeTab === 'urun' ? (
                                                activeSubTab !== 'pasif-urunler' ? (
                                                  <>
                                                    <button
                                                      type="button"
                                                      className="category-action-btn edit-btn"
                                                      onClick={() => handleEditProduct(item)}
                                                      title="Düzenle"
                                                    >
                                                      <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="category-action-btn delete-btn"
                                                      onClick={() => handleDeactivateProduct(item)}
                                                      title="Pasif Yap"
                                                    >
                                                      <i className="fas fa-trash"></i>
                                                    </button>
                                                  </>
                                                ) : (
                                                  <button
                                                    type="button"
                                                    className="category-action-btn move-btn"
                                                    onClick={() => handleActivateProduct(item)}
                                                    title="Aktif Yap"
                                                  >
                                                    <i className="fas fa-check"></i>
                                                  </button>
                                                )
                                              ) : (
                                                activeItemSubTab === 'aktif' ? (
                                                  <>
                                                    <button
                                                      type="button"
                                                      className="category-action-btn edit-btn"
                                                      onClick={() => openEditItemModal(item)}
                                                      title="Düzenle"
                                                    >
                                                      <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button
                                                      type="button"
                                                      className="category-action-btn delete-btn"
                                                      onClick={async () => {
                                                        const itemName = getContentName(item);
                                                        const okPasif = await confirm({
                                                          title: 'Pasifleştir',
                                                          message: `"${itemName}" ${tabLabels[activeTab].toLowerCase()}nı pasif yapmak istediğinize emin misiniz?`,
                                                          variant: 'warning',
                                                          confirmLabel: 'Evet',
                                                        });
                                                        if (!okPasif) {
                                                          return;
                                                        }
                                                        try {
                                                          await deactivateProduct(iid);
                                                          if (window.showNotification) {
                                                            window.showNotification(
                                                              'success',
                                                              `${tabLabels[activeTab]} başarıyla pasifleştirildi`,
                                                            );
                                                          }
                                                          reloadAccordionData();
                                                        } catch (err) {
                                                          console.error('[handleDeactivateItem] Error:', err);
                                                          if (window.showNotification) {
                                                            window.showNotification(
                                                              'error',
                                                              getErrorMessage(err, `${tabLabels[activeTab]} pasifleştirilemedi`),
                                                            );
                                                          }
                                                        }
                                                      }}
                                                      title="Pasif Yap"
                                                    >
                                                      <i className="fas fa-ban"></i>
                                                    </button>
                                                  </>
                                                ) : (
                                                  <button
                                                    type="button"
                                                    className="category-action-btn add-btn"
                                                    onClick={async () => {
                                                      try {
                                                        await activateProduct(iid);
                                                        if (window.showNotification) {
                                                          window.showNotification(
                                                            'success',
                                                            `${tabLabels[activeTab]} başarıyla aktif yapıldı`,
                                                          );
                                                        }
                                                        reloadAccordionData();
                                                      } catch (err) {
                                                        console.error('[handleActivateItem] Error:', err);
                                                        if (window.showNotification) {
                                                          window.showNotification(
                                                            'error',
                                                            getErrorMessage(err, `${tabLabels[activeTab]} aktif yapılamadı`),
                                                          );
                                                        }
                                                      }
                                                    }}
                                                    title="Aktif Yap"
                                                  >
                                                    <i className="fas fa-check"></i>
                                                  </button>
                                                )
                                              )}
                                            </td>
                                            <td className="ctp-table-tasi-col">
                                              <button
                                                type="button"
                                                className="ctp-tasi-icon-btn"
                                                title="Kategoriye taşı"
                                                aria-label="Taşı"
                                                onClick={() => setMoveTargetModal({ kind: 'icerik', item })}
                                              >
                                                <i className="fas fa-arrows-alt" aria-hidden />
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {!isItemUrunMode && paginationMeta.total > 0 && (
                    <div className="records-count" style={{ marginTop: '12px' }}>
                      Toplam {paginationMeta.total} kayıt
                    </div>
                  )}
                      </>
                    );
                    return isItemUrunMode ? (
                      <div
                        className={`ctp-item-urun-split${
                          urunTreeCollapsed ? ' ctp-item-urun-split--tree-collapsed' : ''
                        }${urunLinkedCollapsed ? ' ctp-item-urun-split--linked-collapsed' : ''}`}
                      >
                        <div className="ctp-item-urun-split__tree">
                          {urunTreeCollapsed ? (
                            <div className="ctp-urun-tree-rail">
                              <button
                                type="button"
                                className="ctp-urun-tree-rail-btn"
                                onClick={toggleUrunTreeCollapsed}
                                title="Ürün ağacını göster"
                                aria-expanded={false}
                              >
                                <i className="fas fa-chevron-right" aria-hidden />
                                <span className="ctp-urun-tree-rail-label">Ürün ağacı</span>
                              </button>
                              {selectedProductForParts ? (
                                <span
                                  className="ctp-urun-tree-rail-product"
                                  title={getContentName(selectedProductForParts)}
                                >
                                  {getContentName(selectedProductForParts)}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                          <div className="ctp-urun-tree-box">
                            <div className="ctp-urun-tree-box-header">
                              <div className="ctp-urun-tree-box-header-title-row">
                                <div className="ctp-urun-tree-box-header-title">
                                  <i className="fas fa-sitemap" aria-hidden />
                                  <span>Ürün Ağacı</span>
                                </div>
                                <button
                                  type="button"
                                  className="ctp-urun-tree-collapse-btn"
                                  onClick={toggleUrunTreeCollapsed}
                                  title="Ürün ağacını gizle (parça listesine daha fazla alan)"
                                  aria-label="Ürün ağacını gizle"
                                  aria-expanded
                                >
                                  <i className="fas fa-chevron-left" aria-hidden />
                                </button>
                              </div>
                              <div className="ctp-urun-tree-search ctp-urun-tree-search--block">
                                <i className="fas fa-search" aria-hidden />
                                <input
                                  type="text"
                                  placeholder="Ürün adı, kodu veya mal / model kodu..."
                                  value={urunTreeSearchInput}
                                  onChange={(e) => setUrunTreeSearchInput(e.target.value)}
                                  aria-label="Ürün ağacında ara"
                                  autoComplete="off"
                                />
                                {urunTreeSearchInput ? (
                                  <button
                                    type="button"
                                    onClick={() => setUrunTreeSearchInput('')}
                                    className="ctp-urun-tree-search-clear"
                                    aria-label="Aramayı temizle"
                                  >
                                    <i className="fas fa-times" />
                                  </button>
                                ) : null}
                              </div>
                            </div>
                            <div className="ctp-urun-tree-scroll">{accordionBlock}</div>
                          </div>
                          )}
                        </div>
                        <div className="ctp-item-urun-split__linked">
                          {urunLinkedCollapsed ? (
                            <div className="ctp-urun-linked-rail">
                              <button
                                type="button"
                                className="ctp-urun-linked-rail-btn"
                                onClick={toggleUrunLinkedCollapsed}
                                title={`${tabLabels[activeTab]} listesini göster`}
                                aria-expanded={false}
                              >
                                <i className="fas fa-chevron-left" aria-hidden />
                                <span className="ctp-urun-linked-rail-label">
                                  {tabLabels[activeTab]}lar
                                </span>
                              </button>
                              {selectedProductForParts ? (
                                <span
                                  className="ctp-urun-linked-rail-product"
                                  title={getContentName(selectedProductForParts)}
                                >
                                  {getContentName(selectedProductForParts)}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                          <div className="ctp-linked-parts-section ctp-linked-parts-section--split">
                      <h3 className="ctp-linked-parts-title">
                        <i className="fas fa-puzzle-piece" />
                        <span className="ctp-linked-parts-title-label">
                          Bu ürüne ait {tabLabels[activeTab].toLowerCase()}lar
                        </span>
                        {activeTab === 'parca' && selectedProductForParts ? (
                          <>
                            <button
                              type="button"
                              className="ctp-cat-action-btn ctp-cat-action-btn--excel-export ctp-linked-parts-excel-btn"
                              onClick={() => handleExcelExportUrunParca()}
                              title="Excel ile dışarı aktar"
                            >
                              <i className="fas fa-file-download" />
                            </button>
                            <button
                              type="button"
                              className="ctp-cat-action-btn ctp-cat-action-btn--excel-edit ctp-linked-parts-excel-btn"
                              onClick={() => handleExcelEditUrunParca()}
                              title="Excel ile düzenle"
                            >
                              <i className="fas fa-file-import" />
                            </button>
                            <button
                              type="button"
                              className="ctp-cat-action-btn ctp-cat-action-btn--excel ctp-linked-parts-excel-btn"
                              onClick={() => {
                                const p = selectedProductForParts;
                                const pid = p?.id || p?._id;
                                if (!pid) return;
                                setExcelImportUrunContext({
                                  id: String(pid),
                                  kod: getContentCode(p),
                                  ad: getContentName(p),
                                });
                              }}
                              title="Excel'den aktar"
                            >
                              <i className="fas fa-file-excel" />
                            </button>
                            <button
                              type="button"
                              className="secondary-btn ctp-linked-parts-add-parca-btn"
                              onClick={() => {
                                const p = selectedProductForParts;
                                if (!p) return;
                                const pid = String(p.id || p._id);
                                setCategoryLockedInModal(true);
                                setSelectedCategory(null);
                                setSelectedCategoryName('Kategorisiz');
                                setItemFormMode('create');
                                setSelectedItem(null);
                                setItemFormData({
                                  ...emptyItemFormFields(),
                                  birimId: pickDefaultBirimId(birimler),
                                  bagliUrunOzet: [
                                    {
                                      id: pid,
                                      ad: getContentName(p),
                                      kod: getContentCode(p),
                                    },
                                  ],
                                });
                                setFormError('');
                                setIsItemFormModalOpen(true);
                              }}
                            >
                              <i className="fas fa-plus-circle" /> Parça ekle
                            </button>
                          </>
                        ) : null}
                        <button
                          type="button"
                          className="ctp-urun-linked-collapse-btn"
                          onClick={toggleUrunLinkedCollapsed}
                          title={`${tabLabels[activeTab]} listesini gizle (ağaca daha fazla alan)`}
                          aria-label={`${tabLabels[activeTab]} listesini gizle`}
                          aria-expanded
                        >
                          <i className="fas fa-chevron-right" aria-hidden />
                        </button>
                      </h3>
                      <div className="ctp-linked-parts-scroll">
                      {!selectedProductForParts ? (
                        <div className="ctp-linked-parts-placeholder">
                          <i className="fas fa-hand-pointer" />
                          <span>{`${tabLabels[activeTab]}ları görmek için soldaki ağaçtan bir ürün seçin`}</span>
                        </div>
                      ) : (
                        <>
                          <div className="ctp-linked-parts-header">
                            {selectedProductForParts.fotoUrl ? (
                              <button
                                type="button"
                                className="photo-thumb-btn"
                                onClick={() => setSelectedPhotoUrl(getParcaFotoUrl(selectedProductForParts.fotoUrl))}
                                style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', flexShrink: 0 }}
                                title="Büyüt"
                              >
                                <img
                                  src={getParcaFotoUrl(selectedProductForParts.fotoUrl)}
                                  alt=""
                                  className="ctp-linked-parts-product-photo"
                                />
                              </button>
                            ) : (
                              <div className="ctp-linked-parts-product-photo ctp-linked-parts-product-photo--empty">
                                <i className="fas fa-box-open" />
                              </div>
                            )}
                            <div className="ctp-linked-parts-product-info">
                              <span className="ctp-linked-parts-product-name ctp-linked-parts-product-name--primary">
                                {getContentName(selectedProductForParts)}
                              </span>
                              <div className="ctp-linked-parts-product-meta">
                                <span className="ctp-linked-parts-product-meta-item">
                                  <span className="ctp-linked-parts-product-meta-label">Ürün kodu</span>
                                  <span>{getContentCode(selectedProductForParts)}</span>
                                </span>
                                {(() => {
                                  const mal =
                                    selectedProductForParts.malKodu != null &&
                                    String(selectedProductForParts.malKodu).trim() !== ''
                                      ? String(selectedProductForParts.malKodu).trim()
                                      : null;
                                  if (!mal) return null;
                                  return (
                                    <span className="ctp-linked-parts-product-meta-item">
                                      <span className="ctp-linked-parts-product-meta-label">Mal kodu</span>
                                      <span>{mal}</span>
                                    </span>
                                  );
                                })()}
                                {(() => {
                                  const model =
                                    selectedProductForParts.modelKodu != null &&
                                    String(selectedProductForParts.modelKodu).trim() !== ''
                                      ? String(selectedProductForParts.modelKodu).trim()
                                      : null;
                                  if (!model) return null;
                                  return (
                                    <span className="ctp-linked-parts-product-meta-item">
                                      <span className="ctp-linked-parts-product-meta-label">Model kodu</span>
                                      <span>{model}</span>
                                    </span>
                                  );
                                })()}
                                {selectedProductForParts.kurulumGarantiSuresiAy != null &&
                                Number.isFinite(Number(selectedProductForParts.kurulumGarantiSuresiAy)) ? (
                                  <span className="ctp-linked-parts-product-meta-item">
                                    <span className="ctp-linked-parts-product-meta-label">Ürün garantisi</span>
                                    <span>{selectedProductForParts.kurulumGarantiSuresiAy} ay</span>
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="ctp-linked-parts-close"
                              onClick={() => setSelectedProductForParts(null)}
                              title="Seçimi kaldır"
                            >
                              <i className="fas fa-times" />
                            </button>
                          </div>
                          {linkedPartsLoadingIds.has(String(selectedProductForParts.id || selectedProductForParts._id)) ? (
                            <div className="ctp-linked-parts-loading">
                              <i className="fas fa-spinner fa-spin" />
                              <span>Yükleniyor...</span>
                            </div>
                          ) : (() => {
                            const spId = String(selectedProductForParts.id || selectedProductForParts._id);
                            const parts = sortContentsRows(linkedPartsByProductId[spId] || [], listSort);
                            return parts.length === 0 ? (
                              <div className="ctp-linked-parts-empty">
                                <i className="fas fa-info-circle" />
                                <span>Bu ürüne bağlı {tabLabels[activeTab].toLowerCase()} bulunamadı.</span>
                              </div>
                            ) : (
                              <div className="table-container ctp-linked-parts-table-scroll">
                                <table className="admins-table">
                                  <thead>
                                    <tr>
                                      {activeTab === 'parca' && <th>Foto</th>}
                                      <th>{getFieldNames(activeTab).labelKod}</th>
                                      <th>{getFieldNames(activeTab).labelAdi}</th>
                                      {activeTab === 'parca' && (
                                        <th title="Randevuda değişimde 6 ay parça garantisi">Değ. garanti</th>
                                      )}
                                      {activeTab === 'parca' && <th>Birim</th>}
                                      {(activeTab === 'parca' || activeTab === 'islem') && (
                                        <>
                                          {activeTab === 'parca' ? (
                                            <>
                                              <th>Net fiyat (₺)</th>
                                              <th>Brüt fiyat (₺)</th>
                                            </>
                                          ) : (
                                            <>
                                              <th>Garanti dahili (₺)</th>
                                              <th>Garanti harici (₺)</th>
                                            </>
                                          )}
                                        </>
                                      )}
                                      <th></th>
                                      <th
                                        className="ctp-table-tasi-col"
                                        scope="col"
                                        title="Kategoriye taşı"
                                        aria-label="Taşı"
                                      >
                                        <i className="fas fa-arrows-alt ctp-table-tasi-th-icon" aria-hidden />
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {parts.map((item) => {
                                      const iid = item.id || item._id;
                                      return (
                                        <tr key={iid}>
                                          {activeTab === 'parca' && (
                                            <td>
                                              {item.fotoUrl ? (
                                                <button
                                                  type="button"
                                                  className="photo-thumb-btn"
                                                  onClick={() => setSelectedPhotoUrl(getParcaFotoUrl(item.fotoUrl))}
                                                  style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                                                  title="Büyüt"
                                                >
                                                  <img
                                                    src={getParcaFotoUrl(item.fotoUrl)}
                                                    alt=""
                                                    style={{ width: 48, height: 48, objectFit: 'cover', border: '1px solid var(--border-gray)' }}
                                                  />
                                                </button>
                                              ) : (
                                                <div
                                                  style={{
                                                    width: 48, height: 48,
                                                    border: '1px solid var(--border-gray)',
                                                    background: 'var(--bg-light, #f5f5f5)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: 'var(--text-gray)',
                                                  }}
                                                  title="Fotoğraf yok"
                                                >
                                                  <i className="fas fa-image" style={{ fontSize: 20, opacity: 0.5 }} />
                                                </div>
                                              )}
                                            </td>
                                          )}
                                          <td className="name-cell" style={{ color: 'var(--primary-blue)', fontWeight: 500 }}>
                                            {getContentCode(item)}
                                          </td>
                                          <td style={{ fontWeight: 500 }}>{getContentName(item)}</td>
                                          {activeTab === 'parca' && (
                                            <td style={{ textAlign: 'center' }}>
                                              {item.degisimParcaGarantisi === true ? (
                                                <i className="fas fa-check-circle" style={{ color: '#16a34a' }} aria-label="Evet" />
                                              ) : (
                                                <span style={{ color: 'var(--text-gray)' }}>—</span>
                                              )}
                                            </td>
                                          )}
                                          {activeTab === 'parca' && <td>{getParcaBirimLabel(item)}</td>}
                                          {(activeTab === 'parca' || activeTab === 'islem') && (
                                            <>
                                              {activeTab === 'parca' ? (
                                                <>
                                                  <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyatgaranti)}</td>
                                                  <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyat)}</td>
                                                </>
                                              ) : (
                                                <>
                                                  <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyatGarantiDahili ?? item.fiyatgaranti)}</td>
                                                  <td style={{ fontWeight: 500 }}>{formatContentPrice(item.fiyatGarantiHarici ?? item.fiyat)}</td>
                                                </>
                                              )}
                                            </>
                                          )}
                                          <td className="table-actions">
                                            <button
                                              type="button"
                                              className="category-action-btn edit-btn"
                                              onClick={() => openEditItemModal(item)}
                                              title="Düzenle"
                                            >
                                              <i className="fas fa-edit" />
                                            </button>
                                            {activeItemSubTab === 'aktif' ? (
                                              <button
                                                type="button"
                                                className="category-action-btn delete-btn"
                                                onClick={async () => {
                                                  const itemName = getContentName(item);
                                                  const okPasif = await confirm({
                                                    title: 'Pasifleştir',
                                                    message: `"${itemName}" ${tabLabels[activeTab].toLowerCase()}nı pasif yapmak istediğinize emin misiniz?`,
                                                    variant: 'warning',
                                                    confirmLabel: 'Evet',
                                                  });
                                                  if (!okPasif) return;
                                                  try {
                                                    await deactivateProduct(iid);
                                                    if (window.showNotification) window.showNotification('success', `${tabLabels[activeTab]} başarıyla pasifleştirildi`);
                                                    loadLinkedPartsForProduct(spId);
                                                  } catch (err) {
                                                    if (window.showNotification) window.showNotification('error', getErrorMessage(err, `${tabLabels[activeTab]} pasifleştirilemedi`));
                                                  }
                                                }}
                                                title="Pasif Yap"
                                              >
                                                <i className="fas fa-ban" />
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                className="category-action-btn add-btn"
                                                onClick={async () => {
                                                  try {
                                                    await activateProduct(iid);
                                                    if (window.showNotification) window.showNotification('success', `${tabLabels[activeTab]} başarıyla aktif yapıldı`);
                                                    loadLinkedPartsForProduct(spId);
                                                  } catch (err) {
                                                    if (window.showNotification) window.showNotification('error', getErrorMessage(err, `${tabLabels[activeTab]} aktif yapılamadı`));
                                                  }
                                                }}
                                                title="Aktif Yap"
                                              >
                                                <i className="fas fa-check" />
                                              </button>
                                            )}
                                          </td>
                                          <td className="ctp-table-tasi-col">
                                            <button
                                              type="button"
                                              className="ctp-tasi-icon-btn"
                                              title="Kategoriye taşı"
                                              aria-label="Taşı"
                                              onClick={() => setMoveTargetModal({ kind: 'icerik', item })}
                                            >
                                              <i className="fas fa-arrows-alt" aria-hidden />
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })()}
                        </>
                      )}
                      </div>
                          </div>
                          )}
                        </div>
                      </div>
                    ) : accordionBlock;
                  })()}
                </>
              )}
            </div>
          </div>

      {/* Category Form Modal */}
      {isCategoryFormModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeCategoryFormModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {categoryFormMode === 'create'
                  ? selectedParentCategoryForCreate ? 'Alt Kategori Ekle' : 'Ana Kategori Ekle'
                  : 'Kategori Düzenle'}
              </h2>
              <button type="button" className="close-btn" onClick={closeCategoryFormModal} aria-label="Kapat">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form className="modal-body" onSubmit={handleCategoryFormSubmit}>
              {selectedParentCategoryForCreate && (
                <div className="parent-category-info">
                  <i className="fas fa-info-circle"></i>
                  <span>Üst Kategori: <strong>{selectedParentCategoryForCreate.kategoriAdi}</strong></span>
                </div>
              )}
              <div className="form-grid">
                <label className="form-field">
                  <span>Kategori Adı *</span>
                  <input type="text" value={categoryFormData.kategoriAdi} onChange={(e) => setCategoryFormData({ ...categoryFormData, kategoriAdi: e.target.value })} maxLength={100} required autoFocus />
                </label>
                <label className="form-field">
                  <span>Mal kodu</span>
                  <input type="text" value={categoryFormData.malKodu} onChange={(e) => setCategoryFormData({ ...categoryFormData, malKodu: e.target.value })} maxLength={120} placeholder="Mal Kodu (Opsiyonel)" autoComplete="off" />
                </label>
                <label className="form-field">
                  <span>Model kodu</span>
                  <input type="text" value={categoryFormData.modelKodu} onChange={(e) => setCategoryFormData({ ...categoryFormData, modelKodu: e.target.value })} maxLength={120} placeholder="Model Kodu (Opsiyonel)" autoComplete="off" />
                </label>
              </div>
              {categoryFormError && <div className="error-state small">{categoryFormError}</div>}
              <div className="modal-footer">
                <button type="button" className="secondary-btn" onClick={closeCategoryFormModal} disabled={categoryFormSubmitting}>İptal</button>
                <button type="submit" className="primary-btn" disabled={categoryFormSubmitting}>{categoryFormSubmitting ? 'Kaydediliyor...' : categoryFormMode === 'create' ? 'Ekle' : 'Güncelle'}</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Modal: Ürün Formu — başlık/alt bar sabit, alanlar kayar (parça modalı ile aynı düzen) */}
      {isProductFormModalOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={closeProductFormModal}
        >
          <div
            className="modal-content content-item-form-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                {productFormMode === 'create' ? 'Ürün Ekle' : 'Ürün Düzenle'}
              </h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeProductFormModal}
                aria-label="Kapat"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form className="content-item-form-modal__form" onSubmit={handleFormSubmit}>
              <div className="content-item-form-modal__scroll">
              <div className="form-group">
                <label>Kategori *</label>
                {selectedCategory ? (
                  <div
                    className={`selected-category-wrapper${
                      categoryLockedInModal || productFormMode === 'edit'
                        ? ' selected-category-wrapper--locked'
                        : ''
                    }`}
                  >
                    <div className="selected-category-info">
                      <span className="selected-category-label">Seçili:</span>
                      <span className="selected-category-name">{selectedCategoryName}</span>
                    </div>
                    {!(categoryLockedInModal || productFormMode === 'edit') && (
                      <button
                        type="button"
                        className="change-category-btn"
                        onClick={handleChangeCategory}
                        title="Kategori Değiştir"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="form-control category-select-button"
                    onClick={handleOpenCategorySelect}
                  >
                    <span>Kategori Seç</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="urun-adi">Ürün Adı *</label>
                  <input
                  id="urun-adi"
                  type="text"
                  className="form-control"
                  value={formData.icerikAdi}
                  onChange={(e) =>
                    setFormData({ ...formData, icerikAdi: e.target.value })
                  }
                  placeholder="Ürün adını girin"
                  maxLength={200}
                  required
                />
                <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', display: 'block' }}>
                  {formData.icerikAdi.length} / 200 karakter
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="urun-adi-2">Ürün Adı 2</label>
                <input
                  id="urun-adi-2"
                  type="text"
                  className="form-control"
                  value={formData.urunAdi2}
                  onChange={(e) =>
                    setFormData({ ...formData, urunAdi2: e.target.value })
                  }
                  placeholder="Opsiyonel — listede gösterilmez"
                  maxLength={200}
                  autoComplete="off"
                />
                <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', display: 'block' }}>
                  {formData.urunAdi2.length} / 200 karakter
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="urun-kodu">Ürün Kodu *</label>
                  <input
                  id="urun-kodu"
                  type="text"
                  className="form-control"
                  value={formData.icerikKodu}
                  onChange={(e) =>
                    setFormData({ ...formData, icerikKodu: e.target.value })
                  }
                  placeholder="Ürün kodunu girin (boşluk içeremez)"
                  maxLength={50}
                  required
                />
                <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', display: 'block' }}>
                  {formData.icerikKodu.length} / 50 karakter (boşluk yok)
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="urun-mal-kodu">Mal kodu</label>
                <input
                  id="urun-mal-kodu"
                  type="text"
                  className="form-control"
                  value={formData.malKodu}
                  onChange={(e) => setFormData({ ...formData, malKodu: e.target.value })}
                  placeholder="Mal Kodu (Opsiyonel)"
                  maxLength={120}
                  autoComplete="off"
                />
              </div>

              <div className="form-group">
                <label htmlFor="urun-model-kodu">Model kodu</label>
                <input
                  id="urun-model-kodu"
                  type="text"
                  className="form-control"
                  value={formData.modelKodu}
                  onChange={(e) => setFormData({ ...formData, modelKodu: e.target.value })}
                  placeholder="Model Kodu (Opsiyonel)"
                  maxLength={120}
                  autoComplete="off"
                />
              </div>

              <div className="form-group">
                <label htmlFor="urun-checklist">Kontrol listesi (checklist)</label>
                <select
                  id="urun-checklist"
                  className="form-control"
                  value={formData.checklistId}
                  onChange={(e) =>
                    setFormData({ ...formData, checklistId: e.target.value })
                  }
                >
                  <option value="">Yok — mobilde doğrudan işlem adımı</option>
                  {urunChecklistSecenekleri.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.ad}
                      {c.islemTipi ? ` (${c.islemTipi})` : ''}
                      {c.aktif === false ? ' (pasif şablon)' : ''}
                    </option>
                  ))}
                </select>
                <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '6px', display: 'block' }}>
                  Teknisyen randevusunda Evet/Hayır soru adımı için şablon seçin. Atamayı kaldırmak için «Yok» seçip kaydedin.
                  {formData.checklistId ? (
                    <>
                      {' '}
                      <Link to={`/checklists?checklist=${encodeURIComponent(formData.checklistId)}`}>
                        Şablonu Checklistler sayfasında aç
                      </Link>
                    </>
                  ) : null}
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="urun-aciklamasi">Ürün Açıklaması</label>
                  <textarea
                  id="urun-aciklamasi"
                  className="form-control"
                  rows="4"
                  value={formData.aciklama}
                  onChange={(e) =>
                    setFormData({ ...formData, aciklama: e.target.value })
                  }
                  placeholder="Ürün açıklamasını girin (opsiyonel)"
                  maxLength={2000}
                />
                <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', display: 'block' }}>
                  {formData.aciklama.length} / 2000 karakter
                </small>
              </div>

              <ContentFotoUploadZone
                heading="Ürün görseli"
                sub="Opsiyonel · tanıtım veya tanıma için küçük görsel"
                previewUrl={urunFotoPreviewUrl}
                hasExistingFoto={
                  productFormMode === 'edit' && !!selectedProduct?.fotoUrl && !formData.fotoSil
                }
                hasNewFoto={formData.foto instanceof File}
                canSelectFile={
                  !(productFormMode === 'edit' && selectedProduct?.fotoUrl && !formData.fotoSil)
                }
                onFileSelected={(f) => openImageEditor(f, 'urun')}
                onRemoveExisting={() => setFormData({ ...formData, fotoSil: true })}
                onClearNew={() => setFormData({ ...formData, foto: null })}
              />

              <div className="form-group">
                <label htmlFor="urun-kurulum-garanti-ay">Ürün garantisi — kurulum sonrası (ay)</label>
                <input
                  id="urun-kurulum-garanti-ay"
                  type="number"
                  className="form-control"
                  min={1}
                  max={120}
                  step={1}
                  value={formData.kurulumGarantiSuresiAy}
                  onChange={(e) =>
                    setFormData({ ...formData, kurulumGarantiSuresiAy: e.target.value })
                  }
                  placeholder="24"
                />
                <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', display: 'block' }}>
                  Müşteriye kurulum işlemi tamamlandığında (OTP / randevu akışı) oluşturulan cihazın garanti süresi bu ay
                  değerine göre ayarlanır (1–120). Boş bırakılırsa sunucu varsayılanı (24 ay) kullanılır.
                </small>
              </div>

              {formError && (
                <div className="form-error">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{formError}</span>
                </div>
              )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={closeProductFormModal}
                  disabled={submitting}
                >
                  İptal
                </button>
                <button type="submit" className="primary-btn" disabled={submitting}>
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      <span>Kaydet</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Kategori Seçimi */}
      {isCategorySelectModalOpen && (
        <div
          className="modal-overlay category-select-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            // Sadece overlay'e direkt tıklandığında kapat
            // Ok butonuna tıklandıysa modal'ı kapatma
            if (e.target === e.currentTarget && !e.target.closest('.category-toggle-btn')) {
              closeCategorySelectModal();
            }
          }}
          onMouseDown={(e) => {
            // Ok butonuna tıklandıysa event'i durdur
            if (e.target.closest('.category-toggle-btn')) {
              e.stopPropagation();
            }
          }}
          onWheel={(e) => {
            // Scroll event'lerini durdur - modal içeriği scroll edilebilir
            e.stopPropagation();
          }}
          onScroll={(e) => {
            // Scroll event'lerini durdur
            e.stopPropagation();
          }}
        >
          <div 
            className="modal-content category-select-modal-content" 
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onWheel={(e) => {
              // Modal içeriğindeki scroll event'lerini durdur
              e.stopPropagation();
            }}
            onScroll={(e) => {
              // Modal içeriğindeki scroll event'lerini durdur
              e.stopPropagation();
            }}
          >
            <div className="modal-header">
              <h2>Kategori Seçin</h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeCategorySelectModal}
                aria-label="Kapat"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="category-tree-container">
                {pickerCategories === null ? (
                  <div className="empty-state">
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Kategoriler yükleniyor...</span>
                  </div>
                ) : pickerCategories.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-inbox"></i>
                    <span>Henüz kategori bulunmamaktadır.</span>
                  </div>
                ) : (
                  <div className="category-tree">
                    {renderCategoryTree(buildCategoryTree(pickerCategories, listSort))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Ana Ürün Arama */}
      {isMainProductSearchModalOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={closeMainProductSearchModal}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ana Ürün Ara</h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeMainProductSearchModal}
                aria-label="Kapat"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="main-product-search">Ara</label>
                <input
                  id="main-product-search"
                  type="text"
                  className="form-control"
                  value={mainProductSearchQuery}
                  onChange={(e) => {
                    const query = e.target.value;
                    setMainProductSearchQuery(query);
                    if (query.length >= 2) {
                      handleMainProductSearch(query);
                    } else {
                      setMainProducts([]);
                    }
                  }}
                  placeholder="Ana ürün adı veya kodu ile ara..."
                  autoFocus
                />
              </div>

              {mainProductSearchLoading && (
                <div className="loading-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Aranıyor...</span>
                </div>
              )}

              {!mainProductSearchLoading && mainProductSearchQuery.length >= 2 && mainProducts.length === 0 && (
                <div className="empty-state">
                  <i className="fas fa-search"></i>
                  <span>Sonuç bulunamadı.</span>
                </div>
              )}

              {!mainProductSearchLoading && mainProducts.length > 0 && (
                <div className="main-products-list">
                  {mainProducts.map((product) => (
                    <button
                      key={product.id || product._id}
                      type="button"
                      className="main-product-item"
                      onClick={() => handleSelectMainProduct(product)}
                    >
                      <div className="main-product-info">
                        <h4>{getContentName(product)}</h4>
                        <p>Kod: {getContentCode(product)}</p>
                      </div>
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Parça/İşlem/Arıza Formu */}
      {isItemFormModalOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={closeItemFormModal}
        >
          <div
            className="modal-content content-item-form-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                {itemFormMode === 'create' ? `${tabLabels[activeTab]} Ekle` : `${tabLabels[activeTab]} Düzenle`}
              </h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeItemFormModal}
                aria-label="Kapat"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form className="content-item-form-modal__form" onSubmit={handleItemFormSubmit}>
              <div className="content-item-form-modal__scroll">
              <div className="form-group">
                <label>Kategori *</label>
                {selectedCategory || (activeTab === 'parca' && selectedCategoryName === 'Kategorisiz') ? (
                  <div
                    className={`selected-category-wrapper${
                      categoryLockedInModal || itemFormMode === 'edit'
                        ? ' selected-category-wrapper--locked'
                        : ''
                    }`}
                  >
                    <div className="selected-category-info">
                      <span className="selected-category-label">Seçili:</span>
                      <span className="selected-category-name">{selectedCategoryName}</span>
                    </div>
                    {!(categoryLockedInModal || itemFormMode === 'edit') && (
                      <button
                        type="button"
                        className="change-category-btn"
                        onClick={handleChangeCategory}
                        title="Kategori Değiştir"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="form-control category-select-button"
                    onClick={handleOpenCategorySelect}
                  >
                    <span>Kategori Seç</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                )}
              </div>

              {(activeTab === 'parca' || activeTab === 'ariza') && (
                <div className="form-group">
                  <label>Bağlı ürünler (opsiyonel)</label>
                  <p style={{ fontSize: 12, color: 'var(--text-gray)', margin: '0 0 8px' }}>
                    Bu kaydı ürün bazlı görünüm ve aramada bulunabilir yapmak için bir veya daha fazla ürün seçin.
                    {itemFormMode === 'edit' ? ' Seçimler anında kaydedilir.' : null}
                  </p>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => setIsUrunPickerModalOpen(true)}
                    disabled={bagliUrunSaving}
                  >
                    <i className="fas fa-box" /> Ürün seç…
                  </button>
                  {bagliUrunSaving ? (
                    <p style={{ fontSize: 12, color: 'var(--text-gray)', margin: '8px 0 0' }}>
                      <i className="fas fa-spinner fa-spin" aria-hidden /> Kaydediliyor…
                    </p>
                  ) : null}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                    {(itemFormData.bagliUrunOzet || []).map((r) => (
                      <span
                        key={r.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '4px 10px',
                          borderRadius: 20,
                          background: 'var(--bg-light, #f0f4f8)',
                          border: '1px solid var(--border-gray)',
                          fontSize: 13,
                        }}
                      >
                        <span>
                          <strong>{r.kod}</strong> {r.ad}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            handleBagliUrunChange(
                              (itemFormData.bagliUrunOzet || []).filter((x) => x.id !== r.id),
                            )
                          }
                          disabled={bagliUrunSaving}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: bagliUrunSaving ? 'not-allowed' : 'pointer',
                            padding: 0,
                            lineHeight: 1,
                            fontSize: 18,
                          }}
                          aria-label="Ürün eşlemesini kaldır"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(() => {
                const fields = getFieldNames(activeTab);
                return (
                  <>
                    <div className="form-group">
                      <label htmlFor="item-adi">{fields.labelAdi} *</label>
                      <input
                        id="item-adi"
                        type="text"
                        className="form-control"
                        value={itemFormData.adi}
                        onChange={(e) =>
                          setItemFormData({ ...itemFormData, adi: e.target.value })
                        }
                        placeholder={`${fields.labelAdi} girin`}
                        maxLength={200}
                        required
                      />
                      <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', display: 'block' }}>
                        {itemFormData.adi.length} / 200 karakter
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="item-kodu">{fields.labelKod} *</label>
                      <input
                        id="item-kodu"
                        type="text"
                        className="form-control"
                        value={itemFormData.kodu}
                        onChange={(e) =>
                          setItemFormData({ ...itemFormData, kodu: e.target.value })
                        }
                        placeholder={`${fields.labelKod} girin (boşluk içeremez)`}
                        maxLength={50}
                        required
                      />
                      <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', display: 'block' }}>
                        {itemFormData.kodu.length} / 50 karakter (boşluk yok)
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="item-aciklamasi">{fields.labelAciklamasi}</label>
                      <textarea
                        id="item-aciklamasi"
                        className="form-control"
                        rows="4"
                        value={itemFormData.aciklamasi}
                        onChange={(e) =>
                          setItemFormData({ ...itemFormData, aciklamasi: e.target.value })
                        }
                        placeholder={`${fields.labelAciklamasi} girin (opsiyonel)`}
                        maxLength={2000}
                      />
                      <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', display: 'block' }}>
                        {itemFormData.aciklamasi.length} / 2000 karakter
                      </small>
                    </div>

                    {activeTab === 'parca' && (
                      <div className="form-group">
                        <label htmlFor="item-birim">Birim *</label>
                        <select
                          id="item-birim"
                          className="form-control"
                          value={itemFormData.birimId}
                          onChange={(e) =>
                            setItemFormData({ ...itemFormData, birimId: e.target.value })
                          }
                          required
                        >
                          <option value="">Seçin…</option>
                          {birimler
                            .filter((b) => b.aktif !== false)
                            .map((b) => (
                              <option key={String(b.id || b._id)} value={String(b.id || b._id)}>
                                {b.ad} ({b.kod})
                                {b.ondalikKullan === false ? ' — tam sayı' : ''}
                              </option>
                            ))}
                        </select>
                        <small style={{ display: 'block', marginTop: 8, fontSize: 12, color: 'var(--text-gray)' }}>
                          Birimleri yönetmek için{' '}
                          <Link
                            to="/ayarlar?tab=birim"
                            style={{
                              color: 'var(--primary-blue, #2563eb)',
                              fontWeight: 600,
                              textDecoration: 'underline',
                              textUnderlineOffset: '2px',
                            }}
                          >
                            Ayarlar → Birim
                          </Link>
                          .
                        </small>
                      </div>
                    )}

                    {activeTab === 'parca' && (
                      <div
                        className="form-group"
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          gap: '12px',
                        }}
                      >
                        <input
                          id="item-degisim-parca-garantisi"
                          type="checkbox"
                          checked={!!itemFormData.degisimParcaGarantisi}
                          onChange={(e) =>
                            setItemFormData({ ...itemFormData, degisimParcaGarantisi: e.target.checked })
                          }
                          style={{ flexShrink: 0, marginTop: 3, width: 18, height: 18, cursor: 'pointer' }}
                        />
                        <label htmlFor="item-degisim-parca-garantisi" style={{ margin: 0, fontWeight: 500, cursor: 'pointer', flex: 1, minWidth: 0 }}>
                          Randevuda değişimde 6 ay parça garantisi
                          <small style={{ display: 'block', fontWeight: 400, color: 'var(--text-gray)', marginTop: 4 }}>
                            Sadece işaretlenen parçalar tamamlanan randevularda garanti kaydı oluşturur (hortum vb. için kapalı bırakın).
                          </small>
                        </label>
                      </div>
                    )}

                    {activeTab === 'parca' && (
                      <ContentFotoUploadZone
                        heading="Parça fotoğrafı"
                        sub="Opsiyonel · parça tanıma ve listelerde gösterim için"
                        previewUrl={parcaFotoPreviewUrl}
                        hasExistingFoto={
                          itemFormMode === 'edit' && !!selectedItem?.fotoUrl && !itemFormData.fotoSil
                        }
                        hasNewFoto={itemFormData.foto instanceof File}
                        canSelectFile={
                          !(itemFormMode === 'edit' && selectedItem?.fotoUrl && !itemFormData.fotoSil)
                        }
                        onFileSelected={(f) => openImageEditor(f, 'parca')}
                        onRemoveExisting={() =>
                          setItemFormData({ ...itemFormData, fotoSil: true })
                        }
                        onClearNew={() => setItemFormData({ ...itemFormData, foto: null })}
                      />
                    )}
                    {activeTab === 'parca' && (
                      <>
                        <div className="form-group">
                          <label htmlFor="item-fiyat">Brüt fiyat (₺)</label>
                          <input
                            id="item-fiyat"
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={itemFormData.fiyat}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^[0-9.,]*$/.test(value)) {
                                setItemFormData({ ...itemFormData, fiyat: value });
                              }
                            }}
                            placeholder="Örn: 1250"
                          />
                          <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '6px', display: 'block' }}>
                            Net fiyat sunucuda <strong>Ayarlar → İskonto</strong> oranı ile hesaplanır.
                          </small>
                          {(() => {
                            const raw = String(itemFormData.fiyat || '').trim().replace(',', '.');
                            if (raw === '') return null;
                            const f = Number(raw);
                            if (Number.isNaN(f) || f < 0) return null;
                            const oran = Number(iskontoOrani) || 0;
                            const mult = 1 - oran / 100;
                            const net = mult <= 0 ? 0 : Math.round(f * mult * 100) / 100;
                            return (
                              <small style={{ fontSize: '12px', color: 'var(--text-muted, #6b7280)', marginTop: '6px', display: 'block' }}>
                                Önizleme — Net:{' '}
                                <strong>{net.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</strong>
                              </small>
                            );
                          })()}
                        </div>
                      </>
                    )}
                    {activeTab === 'islem' && (
                      <>
                        <div className="form-group">
                          <label htmlFor="item-fiyat-garanti-dahili">Garanti dahili fiyat (₺)</label>
                          <input
                            id="item-fiyat-garanti-dahili"
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={itemFormData.fiyatGarantiDahili}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^[0-9.,]*$/.test(value)) {
                                setItemFormData({ ...itemFormData, fiyatGarantiDahili: value });
                              }
                            }}
                            placeholder="Garanti dahilinde randevu bedeli"
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="item-fiyat-garanti-harici">Garanti harici fiyat (₺)</label>
                          <input
                            id="item-fiyat-garanti-harici"
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={itemFormData.fiyatGarantiHarici}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^[0-9.,]*$/.test(value)) {
                                setItemFormData({ ...itemFormData, fiyatGarantiHarici: value });
                              }
                            }}
                            placeholder="Garanti harici müşteri tahsilat"
                          />
                        </div>
                        <small style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px', display: 'block' }}>
                          İskonto uygulanmaz; iki fiyat bağımsız girilir. Boş veya 0 olabilir.
                        </small>
                      </>
                    )}
                  </>
                );
              })()}

              {formError && (
                <div className="form-error">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{formError}</span>
                </div>
              )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={closeItemFormModal}
                  disabled={submitting}
                >
                  İptal
                </button>
                <button type="submit" className="primary-btn" disabled={submitting}>
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      <span>Kaydet</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <UrunCokluSecimModal
        key={urunPickerSyncKey}
        isOpen={isUrunPickerModalOpen}
        onClose={() => setIsUrunPickerModalOpen(false)}
        onSelectionChange={handleBagliUrunChange}
        initialSelected={itemFormData.bagliUrunOzet || []}
        aktifFilter={
          activeItemSubTab === 'aktif' ? true : activeItemSubTab === 'pasif' ? false : undefined
        }
        stepTitle="Bağlı ürünleri seçin"
        saving={bagliUrunSaving}
      />

      {excelEditContext ? (
        <IcerikExcelEditModal
          isOpen
          onClose={() => setExcelEditContext(null)}
          contentType={excelEditContext.contentType}
          scope={excelEditContext.scope}
          onApplied={() => {
            reloadAccordionData();
            if (excelEditContext.scope?.type === 'urun' && excelEditContext.scope.urunIcerikId) {
              loadLinkedPartsForProduct(String(excelEditContext.scope.urunIcerikId));
            }
          }}
        />
      ) : null}

      {(excelImportCategory || excelImportUrunContext) && (
        <IcerikExcelImportModal
          isOpen
          onClose={() => {
            setExcelImportCategory(null);
            setExcelImportUrunContext(null);
          }}
          contentType={activeTab}
          kategoriId={excelImportCategory?.id ?? ''}
          categoryName={excelImportCategory?.name ?? ''}
          importMode={excelImportUrunContext ? 'urun' : 'kategori'}
          urunIcerikId={excelImportUrunContext?.id ?? ''}
          urunLabel={
            excelImportUrunContext
              ? `${excelImportUrunContext.kod} — ${excelImportUrunContext.ad}`
              : ''
          }
          onImported={() => {
            reloadAccordionData();
            if (excelImportUrunContext?.id) {
              loadLinkedPartsForProduct(String(excelImportUrunContext.id));
            }
          }}
        />
      )}

      {moveTargetModal && (
        <CategoryHedefSecModal
          isOpen
          onClose={() => {
            setBulkMoveInProgress(false);
            setMoveTargetModal(null);
          }}
          mode={moveTargetModal.kind === 'kategori' ? 'kategori' : 'icerik'}
          kategoriTreeTipi={
            moveTargetModal.kind === 'kategori' ? categoryTipForAccordion : activeTab
          }
          listSort={listSort}
          hideRootTarget={moveTargetModal.kind === 'icerik-bulk'}
          customTitle={
            moveTargetModal.kind === 'icerik-bulk'
              ? moveTargetModal.sourceCategoryId
                ? `Seçilen ${(tabLabels[activeTab] || 'kayıtları').toLowerCase()} taşı`
                : `Kategorisiz ${(tabLabels[activeTab] || 'kayıtları').toLowerCase()} taşı`
              : undefined
          }
          customSubtitle={
            moveTargetModal.kind === 'icerik-bulk'
              ? `${moveTargetModal.ids.length} ${(tabLabels[activeTab] || 'kayıt').toLowerCase()} seçtiğiniz kategoriye atanacak.`
              : undefined
          }
          excludeCategoryId={
            moveTargetModal.kind === 'icerik-bulk' ? moveTargetModal.sourceCategoryId : null
          }
          confirmBusy={bulkMoveInProgress}
          sourceCategoryId={
            moveTargetModal.kind === 'kategori'
              ? String(moveTargetModal.category.id || moveTargetModal.category._id || '')
              : null
          }
          onConfirm={(targetId) => {
            if (moveTargetModal.kind === 'kategori') {
              const sid = moveTargetModal.category.id || moveTargetModal.category._id;
              applyCategoryMove(sid, targetId);
            } else if (moveTargetModal.kind === 'icerik-bulk') {
              return handleBulkMoveParcaUncategorized(targetId, moveTargetModal.ids);
            } else {
              const iid = moveTargetModal.item.id || moveTargetModal.item._id;
              handleMoveContentToCategory(iid, targetId);
            }
          }}
        />
      )}

      {bulkFotoModal && (
        <ParcaBulkFotoModal
          isOpen
          selectedCount={bulkFotoModal.ids.length}
          previewUrl={bulkFotoPreviewUrl}
          selectedFile={bulkFotoFile}
          confirmBusy={bulkFotoInProgress}
          onClose={closeBulkFotoModal}
          onFileSelected={(f) => openImageEditor(f, 'bulk-parca')}
          onClearFile={() => setBulkFotoFile(null)}
          onConfirm={() => handleBulkAssignParcaFoto(bulkFotoModal.ids, bulkFotoFile)}
        />
      )}

      {bulkFiyatModal && (
        <ParcaBulkFiyatModal
          isOpen
          selectedCount={bulkFiyatModal.ids.length}
          confirmBusy={bulkFiyatInProgress}
          onClose={closeBulkFiyatModal}
          onConfirm={(fiyat) => handleBulkAssignParcaFiyat(bulkFiyatModal.ids, fiyat)}
        />
      )}

      {/* Modal: Görsel editör (parça / ürün) */}
      <ImageEditorModal
        isOpen={imageEditorOpen}
        sourceFile={imageEditorSourceFile}
        onConfirm={handleImageEditorConfirm}
        onCancel={closeImageEditor}
      />

      {/* Modal: Parça Fotoğraf (Büyük Resim) */}
      {selectedPhotoUrl && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setSelectedPhotoUrl(null)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: 'calc(90vw - 20px)',
              maxHeight: 'calc(90vh - 20px)',
              padding: 10,
              background: 'var(--bg-white, #fff)',
              borderRadius: 8,
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setSelectedPhotoUrl(null)}
              aria-label="Kapat"
              style={{ position: 'absolute', top: 4, right: 4, zIndex: 2 }}
            >
              <i className="fas fa-times" />
            </button>
            <img
              src={selectedPhotoUrl}
              alt="Parça fotoğrafı"
              style={{
                display: 'block',
                maxWidth: 'calc(90vw - 60px)',
                maxHeight: 'calc(90vh - 60px)',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      )}
    </main>
  );
};

export default ContentTypePage;

